import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TablePagination,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { onAuthStateChanged } from 'firebase/auth';
import { toast } from 'react-toastify';
import { collection, doc, getDocs, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { bankingSyncNowUrl } from '../../utils/apiBase';
import { formatBankingDateTime } from '../../utils/bankingDateTime';
import { matchesBankProviderFilter } from '../../utils/bankingMovementUi';
import {
  branchLabel,
  DRAFT_FILTER,
  INVOICE_DATE_FILTER,
  INVOICE_DATE_FILTER_DEFAULT,
  INVOICE_LIST_PAGE_SIZE,
  INVOICE_MATCH_THRESHOLDS,
  INVOICE_MATCH_TYPE,
  INVOICE_NAV,
  INVOICE_STATUS,
  QUEUE_DATE_FILTER_DEFAULT,
  QUEUE_DATE_FILTER_OPTIONS,
  toPersistedMatchStatus
} from '../../utils/invoiceSchema';
import InvoiceSidebar from './InvoiceSidebar';
import InvoiceOverviewPanel from './InvoiceOverviewPanel';
import InvoiceToInvoicePanel from './InvoiceToInvoicePanel';
import { iv, ivSurfaceSx } from './invoiceTheme';
import {
  amountsEqual,
  cancelInvoiceDraft,
  canMarkInvoiceReady,
  deleteCancelledInvoiceMatch,
  createInvoiceMatch,
  createManualInvoiceMatch,
  createMultiInvoiceMatch,
  createStandaloneInvoiceDraft,
  sumIslemHavaleAmount,
  fetchBankMovements,
  hideBankMovementFromInvoicing,
  fetchHavaleIslemler,
  filterDraftMatches,
  findDuplicateInvoiceMatch,
  getBankingIdForAppSube,
  isActiveInvoiceMatch,
  resolveLiveCustomerStatus,
  markInvoiceDraftReady,
  markInvoiceDraftsReadyBatch,
  revertInvoiceDraftToDraft,
  subscribeBankMovements,
  subscribeHavaleIslemler,
  subscribeInvoiceMatches,
  subscribeInboundInvoices,
  subscribeInvoiceSettings,
  prepareDraftFormForEditing,
  updateInvoiceDraft,
  getDraftMissingFieldLabels,
  recalculateDraftCustomerStatus,
  sanitizeSnapshot
} from '../../utils/invoiceFirestoreService';
import { runAutoInvoiceMatches } from '../../utils/autoInvoiceMatchService';
import SendIcon from '@mui/icons-material/Send';
import { usePriceContext } from '../../context/PriceContext';
import { buildLiveProductBuyPriceTable, setLiveProductBuyPriceTable,
  buildLiveProductPriceTable,
  setLiveProductPriceTable
} from '../../utils/invoiceLivePriceTable';
import { isKrediKartiMatch } from '../../utils/invoiceKrediKartiRule';
import { buildInvoiceLinkStatusMaps, getInvoiceMatchListChipMeta, getInvoiceMatchListLabel, getInvoiceMatchRowBackground, getInvoiceStatusMeta, getMatchStatusMeta } from './invoiceStatusUi';
import {
  normalizeInvoiceDraft,
  createEmptyLineItem,
  buildBranchInvoiceNote,
  getDefaultBranchInvoiceLocation,
  isDraftReadyForSend,
  trimDraftTextFields
} from '../../utils/invoiceDraftUtils';
import InvoiceDraftDetailPanel from './InvoiceDraftDetailPanel';
import InvoiceSettingsPanel from './InvoiceSettingsPanel';
import InvoiceSentPanel from './InvoiceSentPanel';
import InvoiceInboundPanel from './InvoiceInboundPanel';
import InvoiceExpenseNotePanel from './InvoiceExpenseNotePanel';
import {
  createGiderPusulasi,
  setGiderPusulasiKesildi,
  subscribeGiderPusulalari,
  subscribeGiderPusulasiIsaretleri
} from '../../utils/giderPusulasiService';
import { currentMonth, sonAylar, yilAylari } from './InvoicePeriodSelector';
import { syncKolaysoftArchiveNow } from '../../utils/invoiceEdmApi';

import InvoiceReportsPanel from './InvoiceReportsPanel';
import InvoiceIdentityRiskPanel from './InvoiceIdentityRiskPanel';
import InvoiceAmlPanel from './InvoiceAmlPanel';
import { InvoiceAmlRiskChip } from './InvoiceAmlRiskChip';
import { subscribeAmlAlerts } from '../../utils/amlFirestoreService';
import useMediaQuery from '@mui/material/useMediaQuery';
import { lookupAmlSubject } from '../../utils/amlApi';
import {
  refreshInvoiceEdmStatus,
  respondInboundInvoice,
  fetchInvoiceQueue,
  sendInvoiceQueue,
  updateReadyInvoiceDraft,
  approveQueueDrafts,
  syncInboundInvoices
} from '../../utils/invoiceEdmApi';
import { toFriendlyEdmMessage } from '../../utils/edmFriendlyMessages';
import {
  calculateMatchScore,
  findSameNameDifferentAmountIslemler,
  filterIncomingBankMovements,
  filterInvoiceEligibleIslemler,
  filterItemsByInvoiceDate,
  formatMusteriAdi,
  isInvoiceNameSuggestionEligible,
  matchStatusColor,
  filterMatchesBySearch,
  matchStatusLabel,
  nameMatchLevelLabel,
  paginateInvoiceList,
  resolveDraftMatchDate,
  resolveFirestoreDate,
  resolveIslemListDate,
  resolveIslemMusteriName,
  resolveMovementListDate,
  resolveMovementSenderName,
  suggestIslemlerForMovement,
  suggestMultiMatchForMovement,
  suggestMovementsForIslem,
  truncateSenderDisplay
} from './invoiceMatchUtils';
const SUBE_ETIKET = { kavakli_sube: 'Kavaklı', sefakoy_sube: 'Şefaköy Şube', sefakoy_merkez: 'Merkez' };

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const friendlyBankSyncError = (code, message) => {
  const c = String(code || '').toLowerCase();
  const m = String(message || '').toLowerCase();
  if (c.includes('vbh0014') || m.includes('limit')) return 'Günlük banka sorgu limiti aşıldı';
  if (c.includes('permission')) return 'Banka verisine erişim yetkiniz yok';
  if (c.includes('timeout') || m.includes('timeout') || m.includes('ulaşıl') || m.includes('etimedout')) {
    return 'Banka servisine ulaşılamadı';
  }
  return 'Banka verisi alınamadı';
};

const formatDateTime = (value) => formatBankingDateTime(value) || '-';

const panelHeaderSx = {
  px: 1.2,
  py: 0.9,
  borderBottom: '1px solid #e2e8f0',
  bgcolor: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  minHeight: 44
};

const tableHeadCellSx = { fontWeight: 900, bgcolor: '#f1f5f9', fontSize: 11.5, py: 0.8 };

const metricCardSx = (accent) => ({
  borderRadius: 3,
  border: '1px solid #dbe3ef',
  background: '#fff',
  boxShadow: '0 8px 20px rgba(15,23,42,0.06)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${accent} 0%, rgba(255,255,255,0) 100%)`
  }
});

/**
 * Liste başındaki sayaçlar. Eskiden ayrı bir "Genel Bakış" sayfasıydı;
 * artık tıklanınca listeyi süzen filtreler.
 */
/** e-Fatura mı e-Arşiv mi (InvoiceOutboundPanel ile aynı kaynaklar). */
const resolveDocTypeLabel = (match) => {
  const t = String(match?.documentType || match?.providerRef?.documentType || '').toUpperCase();
  if (t === 'EFATURA') return 'e-Fatura';
  if (t === 'EARSIV') return 'e-Arşiv';
  return '—';
};

const QUEUE_TILES = [
  { key: 'toInvoice', label: 'FATURALANDIRILACAK', shortLabel: 'FATURALIK', filter: null, color: '#b45309' },
  { key: 'pending', label: 'ONAY BEKLEYEN', shortLabel: 'BEKLEYEN', statKey: 'pendingApproval', filter: DRAFT_FILTER.PENDING_APPROVAL, color: '#1d4ed8' },
  { key: 'missing', label: 'BİLGİ EKSİK', shortLabel: 'BİLGİ EKSİK', statKey: 'missing', filter: DRAFT_FILTER.MISSING, color: '#b91c1c' },
  { key: 'ready', label: 'ONAYLANDI', shortLabel: 'ONAYLANDI', statKey: 'sendReady', filter: DRAFT_FILTER.SEND_READY, color: '#15803d' }
];

const getActivePersonel = () => {
  try {
    const raw = localStorage.getItem('aktifPersonel');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

function InvoiceModal({ open, onClose, sube }) {
  const { prices } = usePriceContext() || { prices: [] };
  const [movements, setMovements] = useState([]);
  const [islemler, setIslemler] = useState([]);
  const [invoiceMatches, setInvoiceMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [matchDataLoading, setMatchDataLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingMatch, setCreatingMatch] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionMessage, setActionMessage] = useState(null);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [selectedIslemIds, setSelectedIslemIds] = useState([]);
  const [selectedIslem, setSelectedIslem] = useState(null);
  // Personelin günlük işi onay kuyruğu; açılışta oraya düşer.
  const [activeNav, setActiveNav] = useState(INVOICE_NAV.APPROVAL);
  // Telefonda yan menu 220px ile ekranin yarisini yiyor ve icerige 155px kaliyor;
  // her sey ezilip okunmaz hale geliyordu. Dar ekranda ikon moduna (64px)
  // daraltilmis baslar, kullanici isterse acabilir. Genis ekranda eskisi gibi acik.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
  );
  const [draftFilter, setDraftFilter] = useState(DRAFT_FILTER.PENDING_APPROVAL);
  const [queueDateFilter, setQueueDateFilter] = useState(QUEUE_DATE_FILTER_DEFAULT);
  const [queueSearch, setQueueSearch] = useState('');
  const [selectedDraftMatch, setSelectedDraftMatch] = useState(null);
  const [selectedDraftIds, setSelectedDraftIds] = useState([]);
  const [batchApproving, setBatchApproving] = useState(false);
  const [draftForm, setDraftForm] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [statusActionLoading, setStatusActionLoading] = useState(false);
  const [sendActionLoading, setSendActionLoading] = useState(false);
  const [batchSending, setBatchSending] = useState(false);
  const [, setStatusRefreshId] = useState('');
  const [inboundInvoices, setInboundInvoices] = useState([]);
  const [inboundLoading, setInboundLoading] = useState(false);
  // Gelen Faturalar donemi (Giden ile ayni secici). son6 => 6 aylik liste.
  const [inboundMonth, setInboundMonth] = useState(() => currentMonth());
  const [inboundSon6, setInboundSon6] = useState(false);
  const [inboundTumYil, setInboundTumYil] = useState(false);
  // Gider pusulasi donemi (ayni secici)
  const [gpMonth, setGpMonth] = useState(() => currentMonth());
  const [gpSon6, setGpSon6] = useState(false);
  const [gpTumYil, setGpTumYil] = useState(false);
  const [giderPusulalari, setGiderPusulalari] = useState([]);
  // Pusulada saticinin TCKN ve adresi musteri kartindan gelir.
  const [musteriKartlari, setMusteriKartlari] = useState({});
  const [gpSaving, setGpSaving] = useState(false);
  const inboundDonem = useMemo(() => {
    if (inboundTumYil) return yilAylari(inboundMonth.slice(0, 4));
    return inboundSon6 ? sonAylar(inboundMonth, 6) : inboundMonth;
  }, [inboundSon6, inboundTumYil, inboundMonth]);
  const [inboundSyncing, setInboundSyncing] = useState(false);
  const [inboundRespondingId, setInboundRespondingId] = useState('');
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualMatchReason, setManualMatchReason] = useState('');
  const [pendingManualPair, setPendingManualPair] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(INVOICE_DATE_FILTER_DEFAULT);
  const [bankFilter, setBankFilter] = useState('all');
  const [bankPage, setBankPage] = useState(1);
  const [islemPage, setIslemPage] = useState(1);
  const [draftPage, setDraftPage] = useState(1);
  const [amlAlerts, setAmlAlerts] = useState([]);
  /** Telefonda liste ve taslak detayi ayni anda sigmiyor; biri gosterilir. */
  const isPhone = useMediaQuery('(max-width:560px)');
  /** Onaya göndermeden önce AML uyarısı: { items: [{ name, status }], onConfirm } — engellemez, sadece sorar */
  const [amlConfirm, setAmlConfirm] = useState(null);
  const [gpIsaretler, setGpIsaretler] = useState({});
  // Tarih sirasi uyarisi: { payload, onConfirm } — sunucu DATE_ORDER dondurunce.
  const [dateOrderConfirm, setDateOrderConfirm] = useState(null);
  // Ortak GiB kuyrugu: uc subenin onayli faturalari (sunucudan; sube kurali gevsemez)
  const [kuyruk, setKuyruk] = useState([]);
  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [priceFormuller, setPriceFormuller] = useState([]);
  const [genelFiyatAyarlari, setGenelFiyatAyarlari] = useState(null);

  const bankingId = useMemo(() => getBankingIdForAppSube(sube), [sube]);
  const liveHasSellPrice = useMemo(() => {
    const altin = (prices || []).find(
      (p) => String(p?.currencyCode || '').toUpperCase() === 'ALTINTRY'
    );
    return Number(altin?.sell) || 0;
  }, [prices]);
  // Güncel Fiyat Ekranı ile aynı kaynak: formüller + canlı fiyat akışı.
  // "Tutara en yakın ürün" şartı bu tabloyu kullanır.
  const liveProductPrices = useMemo(
    () => buildLiveProductPriceTable(priceFormuller, prices, genelFiyatAyarlari),
    [priceFormuller, prices, genelFiyatAyarlari]
  );
  useEffect(() => {
    setLiveProductPriceTable(liveProductPrices);
  }, [liveProductPrices]);
  // Gider pusulasi ALIS fiyatindan hesaplanir (alim belgesi).
  const liveProductBuyPrices = useMemo(
    () => buildLiveProductBuyPriceTable(priceFormuller, prices, genelFiyatAyarlari),
    [priceFormuller, prices, genelFiyatAyarlari]
  );
  useEffect(() => {
    setLiveProductBuyPriceTable(liveProductBuyPrices);
  }, [liveProductBuyPrices]);
  const invoiceSettingsWithHas = useMemo(
    () => ({ ...(invoiceSettings || {}), liveHasSellPrice, liveProductPrices }),
    [invoiceSettings, liveHasSellPrice, liveProductPrices]
  );
  const canEditInvoiceSettings = Boolean(getActivePersonel()?.id);
  const isApprovalView = activeNav === INVOICE_NAV.APPROVAL;
  const showFullPageLoader = matchesLoading;
  const [autoMatching, setAutoMatching] = useState(false);
  const [autoMatchResult, setAutoMatchResult] = useState(null);
  const autoRunOnOpenRef = useRef('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setFirebaseUser(user));
    return unsub;
  }, []);

  // Fiyat formülleri yalnızca modal açıkken dinlenir.
  useEffect(() => {
    if (!open) return undefined;
    const unsubFormul = onSnapshot(
      collection(db, 'formuller'),
      (snap) => setPriceFormuller(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setPriceFormuller([])
    );
    const unsubAyar = onSnapshot(
      doc(db, 'ayarlar', 'genel_durum'),
      (snap) => setGenelFiyatAyarlari(snap.exists() ? snap.data() : null),
      () => setGenelFiyatAyarlari(null)
    );
    return () => {
      unsubFormul();
      unsubAyar();
    };
  }, [open]);

  const triggerBankSync = useCallback(async () => {
    if (!open || !sube) return;
    try {
      const user =
        firebaseUser ||
        auth.currentUser ||
        (await new Promise((resolve) => {
          const unsub = onAuthStateChanged(auth, (nextUser) => {
            unsub();
            resolve(nextUser);
          });
        }));
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(bankingSyncNowUrl(sube, { allBranches: true, force: true }), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }
      if (!res.ok || data?.ok === false) {
        setLoadError(friendlyBankSyncError(data?.code, data?.message));
      }
    } catch (error) {
      setLoadError(friendlyBankSyncError('network', error?.message || 'Banka senkronu başarısız'));
    }
  }, [open, sube, firebaseUser]);

  const loadOnce = useCallback(async () => {
    if (!open || !sube || !bankingId) return;
    setRefreshing(true);
    setLoadError('');
    try {
      const [bankRows, islemRows] = await Promise.all([
        fetchBankMovements(bankingId),
        fetchHavaleIslemler(sube)
      ]);
      setMovements(bankRows);
      setIslemler(islemRows);
    } catch (err) {
      setLoadError(
        err?.code === 'permission-denied'
          ? 'Bu şubenin verisine erişim yetkiniz yok.'
          : 'Veriler yenilenemedi.'
      );
    } finally {
      setRefreshing(false);
      setMatchDataLoading(false);
    }
  }, [open, sube, bankingId]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    setMatchesLoading(true);
    setLoadError('');

    const buildError = (err, fallback) =>
      err?.code === 'permission-denied'
        ? 'Bu şubenin verisine erişim yetkiniz yok.'
        : fallback;

    const unsubMatches = subscribeInvoiceMatches(
      sube,
      (rows) => {
        setInvoiceMatches(rows);
        setMatchesLoading(false);
      },
      (err) => {
        setLoadError(buildError(err, 'Fatura eşleşmeleri okunamadı.'));
        setMatchesLoading(false);
      }
    );

    return () => unsubMatches();
  }, [open, sube]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    const unsub = subscribeAmlAlerts(sube, (rows) => setAmlAlerts(rows || []), () => setAmlAlerts([]));
    return () => unsub?.();
  }, [open, sube]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    const unsub = subscribeGiderPusulalari(sube, setGiderPusulalari, () => setGiderPusulalari([]));
    return () => unsub();
  }, [open, sube]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    const unsub = subscribeGiderPusulasiIsaretleri(sube, setGpIsaretler, () => setGpIsaretler({}));
    return () => unsub();
  }, [open, sube]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    let alive = true;
    getDocs(collection(db, 'subeler', sube, 'musteriler'))
      .then((snap) => {
        if (!alive) return;
        const harita = {};
        snap.forEach((d) => {
          const f = d.data() || {};
          harita[d.id] = { tcKimlikNo: f.tcKimlikNo || '', adres: f.adres || '' };
        });
        setMusteriKartlari(harita);
      })
      .catch(() => alive && setMusteriKartlari({}));
    return () => {
      alive = false;
    };
  }, [open, sube]);

  const gpKesilmisIslemIds = useMemo(
    () => new Set((giderPusulalari || []).map((p) => String(p.islemId || '')).filter(Boolean)),
    [giderPusulalari]
  );

  const handleSaveGiderPusulasi = useCallback(
    async (form, aday) => {
      const personel = getPersonelPayload();
      if (!personel) throw new Error('Personel oturumu bulunamadı.');
      setGpSaving(true);
      try {
        const sonuc = await createGiderPusulasi({
          sube,
          form,
          islem: aday ? { id: aday.id, islemNo: aday.islemNo } : null,
          personel
        });
        toast.success(`${sonuc.belgeNo} numaralı gider pusulası kaydedildi`);
      } finally {
        setGpSaving(false);
      }
    },
    [sube]
  );

  useEffect(() => {
    if (!open || !sube) return undefined;
    setInboundLoading(true);
    const unsub = subscribeInboundInvoices(
      sube,
      inboundDonem,
      (rows) => {
        setInboundInvoices(rows);
        setInboundLoading(false);
      },
      (err) => {
        setLoadError(
          err?.code === 'permission-denied'
            ? 'Bu şubenin verisine erişim yetkiniz yok.'
            : 'Gelen faturalar okunamadı.'
        );
        setInboundLoading(false);
      }
    );
    return () => unsub();
  }, [open, sube, inboundDonem]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    setSettingsLoading(true);
    const unsub = subscribeInvoiceSettings(
      sube,
      (data) => {
        setInvoiceSettings(data);
        setSettingsLoading(false);
      },
      () => setSettingsLoading(false)
    );
    return unsub;
  }, [open, sube]);

  useEffect(() => {
    if (!open || !sube || !bankingId) return undefined;
    setMatchDataLoading(true);
    loadOnce();
    return undefined;
  }, [open, sube, bankingId, loadOnce]);

  useEffect(() => {
    if (!open) return;
    setSelectedMovement(null);
    setSelectedIslemIds([]);
    setSelectedIslem(null);
    setSelectedDraftMatch(null);
    setDraftForm(null);
    setSaveSuccessMessage('');
  }, [open, sube]);

  // Listeyi canlı tut; formu ezme (bankadan doldurulan TCKN / kullanıcı düzenlemesi silinmesin)
  useEffect(() => {
    if (!selectedDraftMatch?.id) return;
    const fresh = invoiceMatches.find((m) => m.id === selectedDraftMatch.id);
    if (!fresh) return;
    setSelectedDraftMatch((prev) => {
      if (!prev || prev.id !== fresh.id) return fresh;
      return fresh;
    });
  }, [invoiceMatches, selectedDraftMatch?.id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setLoadError('');
    try {
      await triggerBankSync();
      /*
       * Kolaysoft'tan kesilen faturalari da cek. Saatlik cron zaten yapiyor
       * ama portalden fatura kesip hemen gormek isteyen kullanici bir saat
       * beklemesin. Hata yutulur: banka/yerel tazeleme buna bagli degil.
       */
      try {
        const sonuc = await syncKolaysoftArchiveNow({ sube, gunSayisi: 3 });
        if (sonuc?.eklenen > 0) {
          toast.success(`Kolaysoft'tan ${sonuc.eklenen} yeni fatura alındı`);
        }
      } catch (_) {
        /* arsiv cekilemezse liste yine de tazelenir */
      }
      await loadOnce();
    } finally {
      setRefreshing(false);
    }
  };

  const { bankMovementStatusMap, islemStatusMap } = useMemo(
    () => buildInvoiceLinkStatusMaps(invoiceMatches),
    [invoiceMatches]
  );

  const filteredMovements = useMemo(
    () => filterIncomingBankMovements(movements).filter((row) => matchesBankProviderFilter(row, bankFilter)),
    [movements, bankFilter]
  );

  const filteredIslemler = useMemo(() => filterInvoiceEligibleIslemler(islemler), [islemler]);

  const dateFilteredMovements = useMemo(
    () => filterItemsByInvoiceDate(filteredMovements, resolveMovementListDate, dateFilter),
    [filteredMovements, dateFilter]
  );

  const dateFilteredIslemler = useMemo(
    () => filterItemsByInvoiceDate(filteredIslemler, resolveIslemListDate, dateFilter),
    [filteredIslemler, dateFilter]
  );

  const bankPagination = useMemo(
    () => paginateInvoiceList(dateFilteredMovements, bankPage, INVOICE_LIST_PAGE_SIZE),
    [dateFilteredMovements, bankPage]
  );

  const islemPagination = useMemo(
    () => paginateInvoiceList(dateFilteredIslemler, islemPage, INVOICE_LIST_PAGE_SIZE),
    [dateFilteredIslemler, islemPage]
  );

  const paginatedMovements = bankPagination.items;
  const paginatedIslemler = islemPagination.items;

  const matchableMovements = useMemo(
    () => dateFilteredMovements.filter((row) => !bankMovementStatusMap[row.id]?.isLocked),
    [dateFilteredMovements, bankMovementStatusMap]
  );

  const matchableIslemler = useMemo(
    () => dateFilteredIslemler.filter((row) => !islemStatusMap[row.id]?.isLocked),
    [dateFilteredIslemler, islemStatusMap]
  );

  const selectedBankLink = selectedMovement ? bankMovementStatusMap[selectedMovement.id] : null;

  const selectedIslemLink = selectedIslem ? islemStatusMap[selectedIslem.id] : null;

  const hasLockedSelection = useMemo(() => {
    if (selectedBankLink?.isLocked) return true;
    if (selectedIslemLink?.isLocked) return true;
    return selectedIslemIds.some((id) => islemStatusMap[id]?.isLocked);
  }, [selectedBankLink, selectedIslemLink, selectedIslemIds, islemStatusMap]);

  const selectedIslemler = useMemo(
    () => dateFilteredIslemler.filter((row) => selectedIslemIds.includes(row.id)),
    [dateFilteredIslemler, selectedIslemIds]
  );

  const selectedIslemTotal = useMemo(() => sumIslemHavaleAmount(selectedIslemler), [selectedIslemler]);

  const multiAmountDiff = useMemo(() => {
    if (!selectedMovement) return 0;
    return Number(selectedMovement.amount || 0) - selectedIslemTotal;
  }, [selectedMovement, selectedIslemTotal]);

  const multiAmountsMatch = useMemo(
    () => Boolean(selectedMovement && selectedIslemler.length >= 2 && amountsEqual(selectedMovement.amount, selectedIslemTotal)),
    [selectedMovement, selectedIslemler.length, selectedIslemTotal]
  );

  const multiNameMismatch = useMemo(() => {
    if (!selectedMovement || selectedIslemler.length < 2) return false;
    return selectedIslemler.some((islem) => !isInvoiceNameSuggestionEligible(calculateMatchScore(selectedMovement, islem)));
  }, [selectedMovement, selectedIslemler]);

  const sameNameDifferentAmountIslemler = useMemo(() => {
    if (!selectedMovement || selectedBankLink?.isLocked) return [];
    return findSameNameDifferentAmountIslemler(selectedMovement, matchableIslemler);
  }, [selectedMovement, selectedBankLink, matchableIslemler]);

  const multiMatchSuggestion = useMemo(() => {
    if (!selectedMovement || selectedBankLink?.isLocked) return null;
    return suggestMultiMatchForMovement(selectedMovement, matchableIslemler);
  }, [selectedMovement, selectedBankLink, matchableIslemler]);

  const handleApplyMultiMatchSuggestion = () => {
    if (!multiMatchSuggestion?.islemler?.length) return;
    setSelectedIslemIds(multiMatchSuggestion.islemIds);
    setSelectedIslem(null);
    setActionMessage(null);
  };

  useEffect(() => {
    if (selectedIslemIds.length === 1) {
      setSelectedIslem(dateFilteredIslemler.find((row) => row.id === selectedIslemIds[0]) || null);
    } else {
      setSelectedIslem(null);
    }
  }, [selectedIslemIds, dateFilteredIslemler]);

  useEffect(() => {
    if (bankPage > bankPagination.totalPages) {
      setBankPage(bankPagination.totalPages);
    }
  }, [bankPage, bankPagination.totalPages]);

  useEffect(() => {
    if (islemPage > islemPagination.totalPages) {
      setIslemPage(islemPagination.totalPages);
    }
  }, [islemPage, islemPagination.totalPages]);

  // Diger subelerin ONAYLI faturalari Onaylandi listesine eklenir (ortak kuyruk).
  // Kimlik "sube__id": ayni id iki subede olabilir. Kendi subemizinkiler canli
  // dinleyiciden gelir, kuyruktan alinmaz.
  const digerSubeOnaylilar = useMemo(
    () =>
      (kuyruk || [])
        .filter((m) => m.sube && m.sube !== sube)
        .map((m) => ({ ...m, id: `${m.sube}__${m.id}`, _matchId: m.id, _sube: m.sube, _digerSube: true })),
    [kuyruk, sube]
  );
  const listeEslesmeleri = useMemo(
    () => [...(invoiceMatches || []), ...digerSubeOnaylilar],
    [invoiceMatches, digerSubeOnaylilar]
  );

  const queueDateScopedMatches = useMemo(
    () => filterItemsByInvoiceDate(listeEslesmeleri, resolveDraftMatchDate, queueDateFilter),
    [listeEslesmeleri, queueDateFilter]
  );

  const draftStats = useMemo(() => {
    const all = queueDateScopedMatches || [];
    return {
      pendingApproval: all.filter((m) => canMarkInvoiceReady(m, m._sube || sube, invoiceSettingsWithHas)).length,
      missing: all.filter(
        (m) =>
          m.invoiceStatus === INVOICE_STATUS.DRAFT &&
          resolveLiveCustomerStatus(m, m._sube || sube, invoiceSettingsWithHas) === 'missing'
      ).length,
      draft: all.filter((m) => m.invoiceStatus === INVOICE_STATUS.DRAFT).length,
      sendReady: all.filter((m) => m.invoiceStatus === INVOICE_STATUS.READY).length,
      cancelled: all.filter((m) => m.invoiceStatus === INVOICE_STATUS.CANCELLED).length,
      error: all.filter((m) => m.invoiceStatus === INVOICE_STATUS.ERROR).length,
      sent: all.filter(
        (m) => m.invoiceStatus === INVOICE_STATUS.SENT || m.invoiceStatus === INVOICE_STATUS.APPROVED
      ).length,
      approvedGib: all.filter((m) => m.invoiceStatus === INVOICE_STATUS.APPROVED).length
    };
  }, [queueDateScopedMatches, sube, invoiceSettingsWithHas]);

  const filteredDrafts = useMemo(
    () => filterDraftMatches(queueDateScopedMatches, draftFilter, sube, invoiceSettingsWithHas),
    [queueDateScopedMatches, draftFilter, sube, invoiceSettingsWithHas]
  );

  /** Giden / hata görünümlerinde belge sütunları gösterilir. */
  const showDocumentColumns =
    draftFilter === DRAFT_FILTER.SENT || draftFilter === DRAFT_FILTER.ERROR;

  const searchedDrafts = useMemo(
    () => filterMatchesBySearch(filteredDrafts, queueSearch),
    [filteredDrafts, queueSearch]
  );

  const dateFilteredDrafts = useMemo(() => {
    const list = searchedDrafts || [];
    return [...list].sort((a, b) => {
      const ta = resolveDraftMatchDate(a)?.getTime() || 0;
      const tb = resolveDraftMatchDate(b)?.getTime() || 0;
      if (tb !== ta) return tb - ta;
      return String(b.id || '').localeCompare(String(a.id || ''));
    });
  }, [searchedDrafts]);

  const formatDraftListDate = (match) => {
    const d = resolveDraftMatchDate(match);
    if (!d) return '-';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  useEffect(() => {
    setDraftPage(1);
  }, [queueSearch, draftFilter, queueDateFilter]);

  // Secim yalniz o an listede gorunen faturalardan olusur. Sekme/arama/tarih
  // degisince eski secim kaliyordu: Onay Bekliyor'da "15 secildi" yaziyor, 9'u
  // Onaylandi'da kalan gorunmez faturaydi ve GiB'e Gonder onlari da gonderirdi
  // (personel geri bildirimi 07.10.2026).
  useEffect(() => {
    const gorunen = new Set((dateFilteredDrafts || []).map((m) => m.id));
    setSelectedDraftIds((prev) => {
      const kalan = prev.filter((id) => gorunen.has(id));
      return kalan.length === prev.length ? prev : kalan;
    });
  }, [dateFilteredDrafts]);

  const draftPagination = useMemo(
    () => paginateInvoiceList(dateFilteredDrafts, draftPage, INVOICE_LIST_PAGE_SIZE),
    [dateFilteredDrafts, draftPage]
  );
  const paginatedDrafts = draftPagination.items;

  const amlByMatchId = useMemo(() => {
    const rank = { strong: 3, possible: 2, clear: 1 };
    const map = {};
    (amlAlerts || []).forEach((row) => {
      const id = String(row.invoiceMatchId || '').trim();
      if (!id) return;
      // Yönetici incelemesinde çözülen (yanlış eşleşme vb.) uyarı artık risk sayılmaz
      if (row.status === 'resolved') return;
      const status = row.matchStatus || 'clear';
      if ((rank[status] || 0) > (rank[map[id]] || 0)) map[id] = status;
    });
    return map;
  }, [amlAlerts]);

  useEffect(() => {
    if (draftPage > draftPagination.totalPages) {
      setDraftPage(draftPagination.totalPages);
    }
  }, [draftPage, draftPagination.totalPages]);

  useEffect(() => {
    setDraftPage(1);
  }, [queueDateFilter, draftFilter, open, sube]);

  // Filtre değişince listede olmayan seçimi temizle (yanlış detay kalmasın)
  // Yeni Fatura / Giden Belgeler ekranında seçim kasıtlı; kuyruk filtresine bakma.
  useEffect(() => {
    if (!selectedDraftMatch?.id) return;
    if (activeNav === INVOICE_NAV.NEW_INVOICE) return;
    if (activeNav === INVOICE_NAV.OUTBOUND || activeNav === INVOICE_NAV.NEEDS_ACTION) {
      const stillExists = (invoiceMatches || []).some((row) => row.id === selectedDraftMatch.id);
      if (!stillExists) {
        setSelectedDraftMatch(null);
        setDraftForm(null);
        setSaveSuccessMessage('');
      }
      return;
    }
    const stillVisible = (dateFilteredDrafts || []).some((row) => row.id === selectedDraftMatch.id);
    if (!stillVisible) {
      setSelectedDraftMatch(null);
      setDraftForm(null);
      setSaveSuccessMessage('');
    }
  }, [dateFilteredDrafts, invoiceMatches, selectedDraftMatch?.id, activeNav]);

  /**
   * Öneri havuzu — eşleşmemiş TÜM uygun işlemler.
   * Eskiden eşleştirme ekranının "Bugün" varsayılan tarih filtresine bağlıydı;
   * geçmiş hareketler için hiç öneri çıkmıyordu.
   */
  const suggestionIslemler = useMemo(
    () => filteredIslemler.filter((row) => !islemStatusMap[row.id]?.isLocked),
    [filteredIslemler, islemStatusMap]
  );

  const movementSuggestions = useMemo(() => {
    if (!selectedMovement || selectedBankLink?.isLocked) return [];
    return suggestIslemlerForMovement(selectedMovement, suggestionIslemler, { minScore: 50 });
  }, [selectedMovement, selectedBankLink, suggestionIslemler]);

  const islemSuggestions = useMemo(() => {
    if (!selectedIslem || selectedIslemLink?.isLocked) return [];
    return suggestMovementsForIslem(selectedIslem, matchableMovements, { minScore: 50 });
  }, [selectedIslem, selectedIslemLink, matchableMovements]);

  const activePair = useMemo(() => {
    if (selectedMovement && selectedIslem) {
      return calculateMatchScore(selectedMovement, selectedIslem);
    }
    return null;
  }, [selectedMovement, selectedIslem]);

  const activeDuplicate = useMemo(() => {
    if (!selectedMovement && selectedIslemIds.length === 0 && !selectedIslem) return null;
    const bankId = selectedMovement?.id;
    if (bankId && selectedIslemIds.length > 0) {
      return findDuplicateInvoiceMatch(invoiceMatches, bankId, selectedIslemIds);
    }
    const islemId = selectedIslem?.id;
    if (bankId && islemId) {
      return findDuplicateInvoiceMatch(invoiceMatches, bankId, islemId);
    }
    if (bankId) {
      return invoiceMatches.find((m) => m.bankMovementId === bankId && isActiveInvoiceMatch(m)) || null;
    }
    if (islemId) {
      return findDuplicateInvoiceMatch(invoiceMatches, null, islemId);
    }
    return null;
  }, [selectedMovement, selectedIslem, selectedIslemIds, invoiceMatches]);

  const detailSuggestions = selectedMovement
    ? movementSuggestions
    : selectedIslem
      ? islemSuggestions.map((row) => ({
          islem: selectedIslem,
          movement: row.movement,
          score: row.score,
          status: row.status,
          breakdown: row.breakdown
        }))
      : [];

  const handleDateFilterChange = (_, value) => {
    if (!value) return;
    setDateFilter(value);
    setBankPage(1);
    setIslemPage(1);
    setSelectedMovement(null);
    setSelectedIslemIds([]);
    setSelectedIslem(null);
    setActionMessage(null);
  };

  const handleBankFilterChange = (_, value) => {
    if (!value) return;
    setBankFilter(value);
    setBankPage(1);
    setSelectedMovement(null);
    setSelectedIslemIds([]);
    setSelectedIslem(null);
    setActionMessage(null);
  };

  const handleSelectMovement = (row) => {
    setSelectedMovement(row);
    setActionMessage(null);
  };

  const handleSelectIslem = (row) => {
    setSelectedIslemIds([row.id]);
    setActionMessage(null);
  };

  const handleToggleIslemCheckbox = (row, event) => {
    event.stopPropagation();
    if (islemStatusMap[row.id]?.isLocked) return;
    setSelectedIslemIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return [...next];
    });
    setActionMessage(null);
  };

  const getPersonelPayload = () => {
    const personel = getActivePersonel();
    if (!personel?.id) return null;
    return { id: personel.id, isim: personel.isim || '', soyisim: personel.soyisim || '' };
  };

  /**
   * Taslak secimi ASENKRON (musteri karti okunur). Personel listede hizlica
   * fatura fatura gezince onceki secimin gec gelen cevabi yenisinin uzerine
   * biniyor ve ekranda IKI FATURANIN KALEMLERI birlikte gorunuyordu
   * (23.10.2026 personel geri donusu; 1.750 TL'lik faturada baska faturanin
   * 20.926,66 TL'lik satirlari).
   *
   * Cozum: her secime bir sira numarasi verilir; cevap donduğunde secim
   * degismisse sonuc ATILIR. Ayrica yeni secimde form hemen bosaltilir, boylece
   * yeni fatura ile eski formun bir arada gorundugu aralik kapanir.
   */
  const secimSirasiRef = useRef(0);

  const handleSelectDraft = async (match) => {
    const sira = secimSirasiRef.current + 1;
    secimSirasiRef.current = sira;
    const gecerli = () => secimSirasiRef.current === sira;

    if (match?._digerSube) {
      // Baska subenin onayli faturasi: gondermeden once gozle kontrol icin
      // SALT OKUNUR gosterilir. Kayitli taslak aynen (GiB'e gidecek olan);
      // musteri karti vb. bu subeden yeniden okunmaz.
      setSelectedDraftMatch(match);
      setSaveSuccessMessage('');
      setDraftForm(normalizeInvoiceDraft(match.invoiceDraft || {}, invoiceSettingsWithHas));
      return;
    }
    setSelectedDraftMatch(match);
    setSaveSuccessMessage('');
    setDraftForm(null);
    try {
      const form = await prepareDraftFormForEditing({ sube, match, invoiceSettings: invoiceSettingsWithHas });
      if (!gecerli()) return;
      setDraftForm(form);
      // Not: tıklayınca otomatik kaydetmiyoruz — sadece formu doldururuz.
      // Böylece durum sekmeleri tıklamayla kendiliğinden değişmez.
    } catch (_) {
      if (!gecerli()) return;
      setDraftForm(normalizeInvoiceDraft(match?.invoiceDraft || {}, invoiceSettingsWithHas));
    }
  };

  /** Diger subenin taslagini sunucu uzerinden kaydeder; yerel kopyayi gunceller. */
  const digerSubeTaslagiKaydet = async (m, form) => {
    const cleaned = sanitizeSnapshot(trimDraftTextFields(normalizeInvoiceDraft(form || {})));
    const r = await updateReadyInvoiceDraft({
      sube,
      target: { sube: m._sube, matchId: m._matchId },
      invoiceDraft: cleaned,
      customerStatus: recalculateDraftCustomerStatus(cleaned)
    });
    const yeni = { ...m, invoiceDraft: cleaned, customerStatus: recalculateDraftCustomerStatus(cleaned) };
    setSelectedDraftMatch(yeni);
    setDraftForm(normalizeInvoiceDraft(cleaned, invoiceSettingsWithHas));
    return { r, yeni };
  };

  const handleSaveDraft = async () => {
    if (!selectedDraftMatch?.id || !draftForm) return;
    if (selectedDraftMatch._digerSube) {
      setSavingDraft(true);
      try {
        const { r } = await digerSubeTaslagiKaydet(selectedDraftMatch, draftForm);
        toast.success(r?.degisen?.length ? `Kaydedildi (${r.degisen.join(', ')})` : 'Kaydedildi');
        kuyruguYenile();
      } catch (err) {
        toast.error(err?.message || 'Kaydedilemedi');
      } finally {
        setSavingDraft(false);
      }
      return;
    }

    const personel = getActivePersonel();
    if (!personel?.id) {
      toast.error('Personel oturumu bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }

    setSavingDraft(true);
    setSaveSuccessMessage('');
    try {
      const personelPayload = {
        id: personel.id,
        isim: personel.isim || '',
        soyisim: personel.soyisim || ''
      };

      // Yeni Fatura: Kaydet’e basılana kadar Firestore’a yazılmaz
      if (selectedDraftMatch._localOnly) {
        const result = await createStandaloneInvoiceDraft({
          sube,
          createdByPersonel: personelPayload,
          invoiceDraft: draftForm
        });
        // Kaydedilen fatura listeye gecer; ekranda bir sonraki fatura icin
        // bos form acilir (personel geri bildirimi 05.10.2026).
        const ad = String(result?.invoiceDraft?.title || draftForm?.title || '').trim();
        toast.success(
          `${ad ? `${ad} · ` : ''}Fatura kaydedildi → ${
            result.customerStatus === 'complete' ? 'Onay Bekliyor' : 'Bilgi Eksik'
          } listesinde. Yeni form açıldı.`
        );
        handleCreateStandaloneInvoice();
        return;
      }

      const result = await updateInvoiceDraft({
        sube,
        matchId: selectedDraftMatch.id,
        invoiceDraft: draftForm,
        updatedByPersonel: personelPayload
      });

      if (result.customerSyncError) {
        toast.warning(
          'Taslak kaydedildi ancak müşteri kartı güncellenemedi. Müşteri koleksiyonunu kontrol edin.'
        );
      }

      const successText =
        result.customerStatus === 'complete'
          ? 'Taslak kaydedildi · Hazır Taslak'
          : 'Taslak kaydedildi · Bilgi Eksik';
      setSaveSuccessMessage(successText);
      toast.success('Taslak başarıyla kaydedildi.');
      setSelectedDraftMatch((prev) =>
        prev
          ? {
              ...prev,
              customerStatus: result.customerStatus,
              invoiceDraft: result.invoiceDraft
            }
          : prev
      );
      setDraftForm({ ...result.invoiceDraft });
    } catch (err) {
      toast.error(err?.message || 'Taslak kaydedilemedi.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCreateMatch = async (movement, islem, score) => {
    if (!movement?.id || !islem?.id) return;

    const duplicate = findDuplicateInvoiceMatch(invoiceMatches, movement.id, islem.id);
    if (duplicate) {
      setActionMessage({ type: 'warning', text: 'Zaten Taslak Var' });
      return;
    }

    const personel = getPersonelPayload();
    if (!personel) {
      setActionMessage({ type: 'error', text: 'Personel oturumu bulunamadı. Lütfen tekrar giriş yapın.' });
      return;
    }

    setCreatingMatch(true);
    setActionMessage(null);
    try {
      const result = await createInvoiceMatch({
        sube,
        bankingId,
        bankMovement: movement,
        islem,
        matchScore: score,
        matchStatus: toPersistedMatchStatus(score),
        createdByPersonel: personel
      });

      setActionMessage({
        type: result.customerStatus === 'missing' ? 'warning' : 'success',
        text:
          result.customerStatus === 'missing'
            ? 'Taslak Oluşturuldu · Bilgi Eksik'
            : 'Taslak Oluşturuldu'
      });
    } catch (err) {
      if (err?.code === 'DUPLICATE_MATCH') {
        setActionMessage({ type: 'warning', text: 'Zaten Taslak Var' });
      } else {
        setActionMessage({
          type: 'error',
          text: err?.message || 'Eşleştirme kaydı oluşturulamadı.'
        });
      }
    } finally {
      setCreatingMatch(false);
    }
  };

  const openManualMatchDialog = (movement, islem, matchResult) => {
    const resolved =
      typeof matchResult === 'object' && matchResult !== null
        ? matchResult
        : calculateMatchScore(movement, islem);
    setPendingManualPair({
      movement,
      islem,
      score: resolved.score,
      nameMatch: resolved.nameMatch,
      nameMatchLevel: resolved.nameMatchLevel,
      amountMatch: resolved.amountMatch
    });
    setManualMatchReason('');
    setManualDialogOpen(true);
  };

  const handleCreateMultiMatch = async () => {
    if (!selectedMovement?.id || selectedIslemler.length < 2) return;
    if (!multiAmountsMatch) {
      setActionMessage({ type: 'warning', text: 'Seçili işlemlerin toplamı banka hareketiyle eşleşmiyor.' });
      return;
    }

    const duplicate = findDuplicateInvoiceMatch(invoiceMatches, selectedMovement.id, selectedIslemIds);
    if (duplicate) {
      setActionMessage({ type: 'warning', text: 'Zaten Taslak Var' });
      return;
    }

    const personel = getPersonelPayload();
    if (!personel) {
      toast.error('Personel oturumu bulunamadı.');
      return;
    }

    setCreatingMatch(true);
    setActionMessage(null);
    try {
      const result = await createMultiInvoiceMatch({
        sube,
        bankingId,
        bankMovement: selectedMovement,
        islemler: selectedIslemler,
        createdByPersonel: personel
      });
      setActionMessage({
        type: result.customerStatus === 'missing' ? 'warning' : 'success',
        text: `${selectedIslemler.length} işlem çoklu eşleştirildi · taslak oluşturuldu.`
      });
      setSelectedIslemIds([]);
      toast.success('Çoklu taslak oluşturuldu.');
    } catch (err) {
      if (err?.code === 'DUPLICATE_MATCH') {
        setActionMessage({ type: 'warning', text: 'Zaten Taslak Var' });
      } else {
        setActionMessage({ type: 'error', text: err?.message || 'Çoklu eşleştirme başarısız.' });
      }
    } finally {
      setCreatingMatch(false);
    }
  };

  const handleManualMatchConfirm = async () => {
    if (!pendingManualPair?.movement?.id || !pendingManualPair?.islem?.id) return;
    const { movement, islem, score } = pendingManualPair;
    const duplicate = findDuplicateInvoiceMatch(invoiceMatches, movement.id, islem.id);
    if (duplicate) {
      setActionMessage({ type: 'warning', text: 'Zaten Taslak Var' });
      setManualDialogOpen(false);
      return;
    }
    const personel = getPersonelPayload();
    if (!personel) {
      toast.error('Personel oturumu bulunamadı.');
      return;
    }
    setCreatingMatch(true);
    try {
      const result = await createManualInvoiceMatch({
        sube,
        bankingId,
        bankMovement: movement,
        islem,
        matchScore: score,
        manualMatchReason: manualMatchReason,
        createdByPersonel: personel
      });
      setManualDialogOpen(false);
      setActionMessage({
        type: result.customerStatus === 'missing' ? 'warning' : 'success',
        text: 'Manuel eşleştirme ile taslak oluşturuldu.'
      });
      toast.success('Manuel taslak oluşturuldu.');
    } catch (err) {
      if (err?.code === 'DUPLICATE_MATCH') {
        setActionMessage({ type: 'warning', text: 'Zaten Taslak Var' });
      } else {
        toast.error(err?.message || 'Manuel eşleştirme başarısız.');
      }
    } finally {
      setCreatingMatch(false);
    }
  };

  const AML_RISK_RANK = { strong: 2, possible: 1 };

  const handleMarkReady = async () => {
    if (selectedDraftMatch?._localOnly) {
      toast.warning('Önce Kaydet’e basarak faturayı kaydedin.');
      return;
    }
    if (!selectedDraftMatch?.id || !draftForm) return;
    const personel = getPersonelPayload();
    if (!personel) return toast.error('Personel oturumu bulunamadı.');

    // AML: fatura engellenmez; formdaki güncel ad/kimlikle kontrol edilir, eşleşme varsa önce sorulur
    setStatusActionLoading(true);
    let amlStatus = amlByMatchId[selectedDraftMatch.id] || 'clear';
    const name = String(draftForm.title || '').trim();
    const taxId = String(draftForm.vknTckn || '').replace(/\D/g, '');
    if (name.length > 1 || taxId.length >= 10) {
      try {
        const looked = await lookupAmlSubject({ sube, name, taxId });
        if ((AML_RISK_RANK[looked?.status] || 0) > (AML_RISK_RANK[amlStatus] || 0)) amlStatus = looked.status;
      } catch (_) {
        toast.warning('AML kontrolü şu an yapılamadı; fatura yine de onaya gönderilebilir.');
      }
    }
    setStatusActionLoading(false);
    if (AML_RISK_RANK[amlStatus]) {
      setAmlConfirm({
        items: [{ name: name || 'Alıcı', status: amlStatus }],
        onConfirm: () => markReadyNow(personel)
      });
      return;
    }
    await markReadyNow(personel);
  };

  const markReadyNow = async (personel) => {
    if (!selectedDraftMatch?.id || !draftForm) return;
    setStatusActionLoading(true);
    if (selectedDraftMatch._digerSube) {
      try {
        const m = selectedDraftMatch;
        await digerSubeTaslagiKaydet(m, draftForm);
        const r = await approveQueueDrafts({ sube, items: [{ sube: m._sube, matchId: m._matchId }] });
        if (r?.approved) {
          toast.success('Fatura onaya gönderildi.');
          setSaveSuccessMessage('Onay kuyruğuna alındı.');
          setSelectedDraftMatch({ ...m, invoiceStatus: INVOICE_STATUS.READY });
        } else {
          toast.error(r?.results?.[0]?.message || 'Onaylanamadı');
        }
        kuyruguYenile();
      } catch (err) {
        toast.error(err?.message || 'İşlem başarısız.');
      } finally {
        setStatusActionLoading(false);
      }
      return;
    }
    try {
      // Önce formu kaydet (bankadan doldurulan TCKN vb. kalıcı olsun), sonra onayla
      await updateInvoiceDraft({
        sube,
        matchId: selectedDraftMatch.id,
        invoiceDraft: draftForm,
        updatedByPersonel: personel
      });
      await markInvoiceDraftReady({ sube, matchId: selectedDraftMatch.id, updatedByPersonel: personel });
      toast.success('Fatura onaya gönderildi.');
      setSaveSuccessMessage('Onay kuyruğuna alındı.');
      setSelectedDraftIds((prev) => prev.filter((id) => id !== selectedDraftMatch.id));
      if (activeNav === INVOICE_NAV.NEW_INVOICE) {
        handleNavigate(INVOICE_NAV.APPROVAL, { draftFilter: DRAFT_FILTER.SEND_READY });
      }
    } catch (err) {
      toast.error(err?.message || 'İşlem başarısız.');
    } finally {
      setStatusActionLoading(false);
    }
  };

  const handleBatchApprove = async () => {
    // Diger subelerin secili taslaklari sunucu uzerinden onaylanir
    const digerler = selectedDraftIds
      .map((id) => listeEslesmeleri.find((m) => m.id === id))
      .filter((row) => row?._digerSube && canMarkInvoiceReady(row, row._sube, invoiceSettingsWithHas));
    if (digerler.length) {
      try {
        const r = await approveQueueDrafts({ sube, items: digerler.map((m) => ({ sube: m._sube, matchId: m._matchId })) });
        if (r?.approved) toast.success(`${r.approved} fatura (diğer şube) onaya gönderildi.`);
        if (r?.failed) toast.warning(`${r.failed} fatura onaylanamadı (eksik bilgi).`);
        kuyruguYenile();
      } catch (err) {
        toast.error(err?.message || 'Onay başarısız');
      }
    }
    const ids = selectedDraftIds.filter((id) => {
      const row = invoiceMatches.find((m) => m.id === id);
      return row && canMarkInvoiceReady(row, sube, invoiceSettingsWithHas);
    });
    if (!ids.length && digerler.length) {
      setSelectedDraftIds([]);
      return;
    }
    if (!ids.length) {
      toast.warning('Onaya gönderilebilir fatura seçilmedi.');
      return;
    }
    const personel = getPersonelPayload();
    if (!personel) return toast.error('Personel oturumu bulunamadı.');
    const risky = ids
      .filter((id) => AML_RISK_RANK[amlByMatchId[id]])
      .map((id) => {
        const row = invoiceMatches.find((m) => m.id === id);
        return { name: String(row?.invoiceDraft?.title || '').trim() || id, status: amlByMatchId[id] };
      });
    if (risky.length) {
      setAmlConfirm({ items: risky, onConfirm: () => batchApproveNow(ids, personel) });
      return;
    }
    await batchApproveNow(ids, personel);
  };

  const batchApproveNow = async (ids, personel) => {
    setBatchApproving(true);
    try {
      const result = await markInvoiceDraftsReadyBatch({
        sube,
        matchIds: ids,
        updatedByPersonel: personel
      });
      if (result.ok > 0) {
        toast.success(`${result.ok} fatura onaya gönderildi.`);
      }
      if (result.failed.length) {
        toast.warning(`${result.failed.length} kayıt gönderilemedi (eksik bilgi).`);
      }
      setSelectedDraftIds([]);
      setDraftFilter(DRAFT_FILTER.SEND_READY);
    } catch (err) {
      toast.error(err?.message || 'Toplu onay başarısız.');
    } finally {
      setBatchApproving(false);
    }
  };

  const handleToggleDraftSelect = (matchId, event) => {
    event?.stopPropagation?.();
    setSelectedDraftIds((prev) =>
      prev.includes(matchId) ? prev.filter((id) => id !== matchId) : [...prev, matchId]
    );
  };

  const approvableFilteredIds = useMemo(
    () =>
      dateFilteredDrafts
        .filter((m) => canMarkInvoiceReady(m, m._sube || sube, invoiceSettingsWithHas))
        .map((m) => m.id),
    [dateFilteredDrafts, sube, invoiceSettingsWithHas]
  );

  const allApprovableSelected =
    approvableFilteredIds.length > 0 &&
    approvableFilteredIds.every((id) => selectedDraftIds.includes(id));

  const handleToggleSelectAllApprovable = () => {
    if (allApprovableSelected) {
      setSelectedDraftIds((prev) => prev.filter((id) => !approvableFilteredIds.includes(id)));
    } else {
      setSelectedDraftIds((prev) => [...new Set([...prev, ...approvableFilteredIds])]);
    }
  };

  // Onaylandi sekmesi: secim onaylanmis (READY) faturalar uzerinden; bunlar
  // canMarkInvoiceReady'ye girmedigi icin eskiden hic secilemiyordu.
  const isSendReadyFilter = draftFilter === DRAFT_FILTER.SEND_READY;
  const sendableFilteredIds = useMemo(
    () => dateFilteredDrafts.filter((m) => m.invoiceStatus === INVOICE_STATUS.READY).map((m) => m.id),
    [dateFilteredDrafts]
  );
  const allSendableSelected =
    sendableFilteredIds.length > 0 && sendableFilteredIds.every((id) => selectedDraftIds.includes(id));
  const handleToggleSelectAllSendable = () => {
    if (allSendableSelected) {
      setSelectedDraftIds((prev) => prev.filter((id) => !sendableFilteredIds.includes(id)));
    } else {
      setSelectedDraftIds((prev) => [...new Set([...prev, ...sendableFilteredIds])]);
    }
  };
  const batchSendProviderOk =
    ['kolaysoft', 'edm'].includes(String(invoiceSettingsWithHas?.provider || '').toLowerCase()) &&
    invoiceSettingsWithHas?.enabled !== false;

  const handleManualAutoMatch = async ({ silent = false } = {}) => {
    if (invoiceSettings?.automationEnabled === false) {
      if (!silent) toast.warning('Fatura otomasyonu durdurulmuş. Ayarlardan açabilirsiniz.');
      return;
    }
    if (invoiceSettings?.automaticMatching === false) {
      if (!silent) toast.info('Otomatik eşleştirme kapalı.');
      return;
    }
    const personel = getPersonelPayload();
    setCreatingMatch(true);
    setAutoMatching(true);
    try {
      // Her tıklamada taze banka + işlem oku (yeni satış/havale kaçmasın)
      const [bankRows, islemRows] = await Promise.all([
        fetchBankMovements(bankingId),
        fetchHavaleIslemler(sube)
      ]);
      setMovements(bankRows);
      setIslemler(islemRows);
      const result = await runAutoInvoiceMatches({
        sube,
        bankingId,
        movements: bankRows,
        islemler: islemRows,
        existingMatches: invoiceMatches,
        createdByPersonel: personel || undefined,
        // CANLI HAS FİYATI ŞART: ürün tipi şartları gramı
        // "tutar ÷ (has × milyem + işçilik)" ile buluyor. Buradan
        // geçirilmezse servis Firestore ayarlarını okuyor, o ayarlarda canlı
        // fiyat yok ve has 0 kalıyordu; işlemin kendi hasFiyati de 0 ise
        // (bakiye düzeltme kayıtları) gram yalnız işçilikten hesaplanıp
        // 50.000 TL'lik fatura "238,1 gram × 199,5 TL" çıkıyordu
        // (27.10.2026 personel geri dönüşü, SERPİL ÇAMCI).
        invoiceSettings: invoiceSettingsWithHas
      });
      setAutoMatchResult(result);
      if (result.created > 0) {
        const parts = [];
        if (result.singleCreated > 0) parts.push(`${result.singleCreated} tek işlem`);
        if (result.multiCreated > 0) parts.push(`${result.multiCreated} çoklu işlem`);
        if (result.grupCreated > 0) parts.push(`${result.grupCreated} grup`);
        if (result.krediKartiCreated > 0) parts.push(`${result.krediKartiCreated} kredi kartı`);
        toast.success(`${result.created} fatura otomatik oluşturuldu${parts.length ? ` (${parts.join(', ')})` : ''}`);
        if (!silent) {
          setActiveNav(INVOICE_NAV.APPROVAL);
          setDraftFilter(DRAFT_FILTER.PENDING_APPROVAL);
        }
      } else if (!silent) {
        /*
         * Eslesme bulunup KAYIT sirasinda hata alinirsa `created` 0 kalir.
         * Onceden her iki durumda da "bulunamadi" deniyordu; gercek sebep
         * (ornegin tutar uyusmazligi ya da yetki hatasi) kullaniciya hic
         * ulasmiyor, sorun "eslesme yok" sanilip yanlis yerde araniyordu.
         */
        if (result.errors?.length) {
          toast.error(`Eşleşme kurulamadı: ${result.errors[0].message}`);
        } else if (result.skipped > 0) {
          toast.info(`${result.skipped} eşleşme zaten mevcut olduğu için atlandı`);
        } else {
          toast.info('Yeni otomatik eşleşme bulunamadı');
        }
      }
    } catch (err) {
      if (!silent) toast.error(err?.message || 'Otomatik eşleştirme başarısız');
    } finally {
      setCreatingMatch(false);
      setAutoMatching(false);
    }
  };

  // Açılışta bir kez otomatik eşleştir (ayar açıksa)
  useEffect(() => {
    if (!open || !sube || !bankingId) {
      autoRunOnOpenRef.current = '';
      return undefined;
    }
    if (matchesLoading || settingsLoading || matchDataLoading) return undefined;
    if (!invoiceSettings) return undefined;
    if (invoiceSettings.automationEnabled === false) return undefined;
    if (invoiceSettings.automaticMatching === false) return undefined;
    if (invoiceSettings.runMatchOnOpen === false) return undefined;
    if (invoiceSettings.automaticDraft === false) return undefined;

    const runKey = `${sube}:${bankingId}:${open}`;
    if (autoRunOnOpenRef.current === runKey) return undefined;
    autoRunOnOpenRef.current = runKey;

    const timer = setTimeout(() => {
      handleManualAutoMatch({ silent: true });
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    sube,
    bankingId,
    matchesLoading,
    settingsLoading,
    matchDataLoading,
    invoiceSettings?.automationEnabled,
    invoiceSettings?.automaticMatching,
    invoiceSettings?.runMatchOnOpen,
    invoiceSettings?.automaticDraft
  ]);

  const kuyruguYenile = useCallback(async () => {
    if (!open || !sube) return;
    try {
      const r = await fetchInvoiceQueue({ sube });
      setKuyruk(Array.isArray(r?.items) ? r.items : []);
    } catch (_) {
      /* liste kendi subesiyle calismaya devam eder */
    }
  }, [open, sube]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    kuyruguYenile();
    const t = setInterval(kuyruguYenile, 60000);
    return () => clearInterval(t);
  }, [open, sube, kuyruguYenile]);

  /**
   * Ortak kuyruktan gonderim (tekil ve toplu): sunucu tarih sirasiyla gonderir.
   * Daha eski tarihli onayli fatura varsa uyari penceresi "once bunlari da
   * gonder" secenegini sunar.
   */
  const kuyruktanGonder = async (items, { ignoreDateOrder = false } = {}) => {
    const result = await sendInvoiceQueue({ sube, items, ignoreDateOrder });
    const sent = result?.sent || 0;
    const failed = result?.failed || 0;
    const ilkHata = (result?.results || []).find((r) => !r.ok);
    if (sent) toast.success(`${sent} fatura gönderildi`);
    if (failed) toast.warning(`${failed} fatura gönderilemedi${ilkHata?.message ? `: ${toFriendlyEdmMessage(ilkHata.message)}` : ''}`);
    kuyruguYenile();
    return result;
  };

  const tarihSirasiUyarisi = (err, items, devam) => {
    const payload = err.payload || {};
    setDateOrderConfirm({
      payload,
      onConfirm: () => devam(items, { ignoreDateOrder: true }),
      // Eski tarihli onaylilari da ekleyip tarih sirasiyla gonder
      onIncludeOlder:
        (payload.oncekiBekleyenler || []).length && !(payload.dahaYeniKesilmis || []).length
          ? () =>
              devam(
                [...payload.oncekiBekleyenler.map((f) => ({ sube: f.sube, matchId: f.matchId })), ...items],
                { ignoreDateOrder: false }
              )
          : null
    });
  };

  /** Onayli faturada son rotus — her subeden; onayli kalir, gecmise yazilir. */
  const handleSaveReadyEdit = async (form) => {
    const m = selectedDraftMatch;
    if (!m?.id) return false;
    const cleaned = sanitizeSnapshot(trimDraftTextFields(normalizeInvoiceDraft(form || {})));
    if (!isDraftReadyForSend(cleaned)) {
      const eksik = getDraftMissingFieldLabels(cleaned);
      toast.error(`Kaydedilemedi: ${eksik.length ? eksik.join(', ') : 'fatura gönderilebilir durumda değil'}.`);
      return false;
    }
    setSavingDraft(true);
    try {
      const r = await updateReadyInvoiceDraft({
        sube,
        target: { sube: m._sube || sube, matchId: m._matchId || m.id },
        invoiceDraft: cleaned,
        customerStatus: recalculateDraftCustomerStatus(cleaned)
      });
      toast.success(r?.degisen?.length ? `Kaydedildi (${r.degisen.join(', ')})` : 'Kaydedildi');
      if (m._digerSube) {
        // Baska subenin kaydi canli dinlenmiyor: yerel kopyayi guncelle, kuyrugu tazele
        const yeni = { ...m, invoiceDraft: cleaned };
        setSelectedDraftMatch(yeni);
        setDraftForm(normalizeInvoiceDraft(cleaned, invoiceSettingsWithHas));
        kuyruguYenile();
      }
      return true;
    } catch (err) {
      toast.error(err?.message || 'Kaydedilemedi');
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCancelReadyEdit = () => {
    if (selectedDraftMatch) handleSelectDraft(selectedDraftMatch);
  };

  const handleSendToEdm = async () => {
    if (!selectedDraftMatch?.id) return;
    const gonder = async (items, opts) => {
      setSendActionLoading(true);
      try {
        const result = await kuyruktanGonder(items, opts);
        setSaveSuccessMessage(result?.sent ? 'GİB gönderimi tamamlandı' : '');
        if (result?.sent) setActiveNav(INVOICE_NAV.OUTBOUND);
      } catch (err) {
        if (err?.code === 'DATE_ORDER') {
          tarihSirasiUyarisi(err, items, gonder);
          return;
        }
        toast.error(toFriendlyEdmMessage(err?.message || 'Gönderim başarısız'));
      } finally {
        setSendActionLoading(false);
      }
    };
    await gonder(
      [{ sube: selectedDraftMatch._sube || sube, matchId: selectedDraftMatch._matchId || selectedDraftMatch.id }],
      {}
    );
  };

  const handleRefreshEdmStatus = async (matchOverride = null) => {
    const target = matchOverride || selectedDraftMatch;
    if (!target?.id) return;
    setStatusRefreshId(target.id);
    try {
      const result = await refreshInvoiceEdmStatus({ sube, matchId: target.id });
      toast.success(toFriendlyEdmMessage(result?.message || 'Durum güncellendi'));
    } catch (err) {
      toast.error(toFriendlyEdmMessage(err?.message || 'Durum sorgu başarısız'));
    } finally {
      setStatusRefreshId('');
    }
  };

  const handleSyncInbound = async () => {
    setInboundSyncing(true);
    try {
      const result = await syncInboundInvoices({ sube, days: 15 });
      toast.success(result?.message || 'Gelen faturalar senkronize edildi');
    } catch (err) {
      toast.error(toFriendlyEdmMessage(err?.message || 'Gelen fatura senkronu başarısız'));
    } finally {
      setInboundSyncing(false);
    }
  };

  const handleRespondInbound = async (row, decision) => {
    if (!row?.id) return;
    if (decision === 'RED') {
      const ok = window.confirm('Bu gelen faturayı reddetmek istediğinize emin misiniz?');
      if (!ok) return;
    }
    setInboundRespondingId(row.id);
    try {
      const result = await respondInboundInvoice({ sube, docId: row.id, decision });
      toast.success(result?.message || (decision === 'RED' ? 'Reddedildi' : 'Kabul edildi'));
    } catch (err) {
      toast.error(toFriendlyEdmMessage(err?.message || 'Yanıt gönderilemedi'));
    } finally {
      setInboundRespondingId('');
    }
  };

  const handleBatchSendToEdm = async () => {
    const items = (selectedDraftIds || [])
      .map((id) => listeEslesmeleri.find((row) => row.id === id))
      .filter((m) => m && (m.invoiceStatus === INVOICE_STATUS.READY || m.invoiceStatus === INVOICE_STATUS.ERROR))
      .map((m) => ({ sube: m._sube || sube, matchId: m._matchId || m.id }));
    if (!items.length) {
      toast.info('Gönderilecek onaylı fatura seçin (Onaylandı filtresi).');
      return;
    }
    const gonder = async (liste, opts) => {
      setBatchSending(true);
      try {
        const result = await kuyruktanGonder(liste, opts);
        setSelectedDraftIds([]);
        if (result?.sent) setActiveNav(INVOICE_NAV.OUTBOUND);
      } catch (err) {
        if (err?.code === 'DATE_ORDER') {
          tarihSirasiUyarisi(err, liste, gonder);
          return;
        }
        toast.error(toFriendlyEdmMessage(err?.message || 'Toplu gönderim başarısız'));
      } finally {
        setBatchSending(false);
      }
    };
    await gonder(items, {});
  };

  const handleRevertToDraft = async () => {
    if (!selectedDraftMatch?.id) return;
    const personel = getPersonelPayload();
    if (!personel) return toast.error('Personel oturumu bulunamadı.');
    setStatusActionLoading(true);
    try {
      await revertInvoiceDraftToDraft({ sube, matchId: selectedDraftMatch.id, updatedByPersonel: personel });
      toast.success('Taslak draft durumuna alındı.');
      setSaveSuccessMessage('Taslak düzenleme moduna geri alındı.');
    } catch (err) {
      toast.error(err?.message || 'İşlem başarısız.');
    } finally {
      setStatusActionLoading(false);
    }
  };

  const handleCancelDraft = async (cancelReason) => {
    // Kaydedilmemiş yeni fatura: DB’ye hiç yazılmadı — sadece formu kapat
    if (selectedDraftMatch?._localOnly) {
      setSelectedDraftMatch(null);
      setDraftForm(null);
      setSaveSuccessMessage('');
      setActiveNav(INVOICE_NAV.OVERVIEW);
      toast.info('Kaydedilmeden çıkıldı.');
      return true;
    }

    if (!selectedDraftMatch?.id) return false;
    const personel = getPersonelPayload();
    if (!personel) {
      toast.error('Personel oturumu bulunamadı.');
      return false;
    }
    setStatusActionLoading(true);
    try {
      await cancelInvoiceDraft({
        sube,
        matchId: selectedDraftMatch.id,
        cancelReason,
        cancelledByPersonel: personel
      });
      toast.success('Taslak iptal edildi.');
      setSaveSuccessMessage('');
      setDraftFilter(DRAFT_FILTER.CANCELLED);
      return true;
    } catch (err) {
      toast.error(err?.message || 'İptal başarısız.');
      return false;
    } finally {
      setStatusActionLoading(false);
    }
  };

  const handleDeleteCancelledDraft = async () => {
    if (!selectedDraftMatch?.id) return false;
    if (selectedDraftMatch.invoiceStatus !== INVOICE_STATUS.CANCELLED) {
      toast.error('Yalnızca iptal edilmiş taslaklar silinebilir.');
      return false;
    }
    setStatusActionLoading(true);
    try {
      await deleteCancelledInvoiceMatch({ sube, matchId: selectedDraftMatch.id });
      toast.success('İptal edilen taslak silindi');
      setSaveSuccessMessage('');
      setSelectedDraftMatch(null);
      setDraftForm(null);
      setSelectedDraftIds((prev) => prev.filter((id) => id !== selectedDraftMatch.id));
      return true;
    } catch (err) {
      toast.error(err?.message || 'Silme başarısız.');
      return false;
    } finally {
      setStatusActionLoading(false);
    }
  };

  const cancelledFilteredIds = useMemo(
    () =>
      dateFilteredDrafts
        .filter((m) => m.invoiceStatus === INVOICE_STATUS.CANCELLED)
        .map((m) => m.id),
    [dateFilteredDrafts]
  );

  const allCancelledSelected =
    cancelledFilteredIds.length > 0 &&
    cancelledFilteredIds.every((id) => selectedDraftIds.includes(id));

  const handleToggleSelectAllCancelled = () => {
    if (allCancelledSelected) {
      setSelectedDraftIds((prev) => prev.filter((id) => !cancelledFilteredIds.includes(id)));
    } else {
      setSelectedDraftIds((prev) => [...new Set([...prev, ...cancelledFilteredIds])]);
    }
  };

  const handleBatchDeleteCancelled = async () => {
    const ids = selectedDraftIds.filter((id) => cancelledFilteredIds.includes(id));
    if (!ids.length) {
      toast.warning('Silmek için iptal edilmiş fatura seçin.');
      return;
    }
    const ok = window.confirm(`${ids.length} iptal edilmiş fatura kalıcı olarak silinsin mi?`);
    if (!ok) return;
    setStatusActionLoading(true);
    let deleted = 0;
    let failed = 0;
    try {
      for (const matchId of ids) {
        try {
          await deleteCancelledInvoiceMatch({ sube, matchId });
          deleted += 1;
        } catch {
          failed += 1;
        }
      }
      if (deleted) toast.success(`${deleted} fatura silindi`);
      if (failed) toast.warning(`${failed} kayıt silinemedi`);
      setSelectedDraftIds([]);
      if (selectedDraftMatch && ids.includes(selectedDraftMatch.id)) {
        setSelectedDraftMatch(null);
        setDraftForm(null);
      }
    } finally {
      setStatusActionLoading(false);
    }
  };

  /**
   * Faturalandırılacak — eşleşmemiş gelen banka hareketleri.
   *
   * Eskiden eşleştirme ekranının kendi tarih filtresine (varsayılanı "Bugün")
   * bağlıydı; bu yüzden dün gelen ve bekleyen hareketler sayaçta 0 görünüyordu.
   * Artık kuyruğun üstündeki tarih filtresini izler (varsayılanı "Tümü").
   */
  /** Kuyruktan elle çıkarılan hareketler (banka kaydı silinmez, bkz. ayarlar). */
  const hiddenMovementIds = useMemo(
    () => new Set((invoiceSettings?.hiddenBankMovementIds || []).map(String)),
    [invoiceSettings]
  );

  const unmatchedMovements = useMemo(() => {
    const scoped = filterItemsByInvoiceDate(filteredMovements, resolveMovementListDate, queueDateFilter);
    return scoped.filter(
      (row) => !bankMovementStatusMap[row.id]?.isLocked && !hiddenMovementIds.has(String(row.id))
    );
  }, [filteredMovements, queueDateFilter, bankMovementStatusMap, hiddenMovementIds]);

  const handleHideMovement = useCallback(
    async (movement) => {
      try {
        await hideBankMovementFromInvoicing(sube, movement.id);
        toast.success('Hareket faturalandırılacaklardan çıkarıldı');
      } catch (err) {
        toast.error(err?.message || 'Hareket gizlenemedi');
      }
    },
    [sube]
  );

  const overviewStats = useMemo(() => {
    const pendingRows = (queueDateScopedMatches || []).filter((m) =>
      canMarkInvoiceReady(m, sube, invoiceSettingsWithHas)
    );
    const pendingApprovalAmount = pendingRows.reduce((sum, m) => {
      const t = Number(m?.invoiceDraft?.invoiceTotals?.genelToplam ?? m?.bankMovementData?.amount ?? 0);
      return sum + (Number.isFinite(t) ? t : 0);
    }, 0);
    const unmatchedAmount = unmatchedMovements.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    // İşlem gerekenlerde eski yığılmayı azalt: son 15 gün
    const recentMatches = filterItemsByInvoiceDate(
      queueDateScopedMatches || [],
      resolveDraftMatchDate,
      INVOICE_DATE_FILTER.LAST_15_DAYS
    );
    const recentMissing = recentMatches.filter(
      (m) =>
        m.invoiceStatus === INVOICE_STATUS.DRAFT &&
        resolveLiveCustomerStatus(m, sube, invoiceSettingsWithHas) === 'missing'
    ).length;
    const recentError = recentMatches.filter((m) => m.invoiceStatus === INVOICE_STATUS.ERROR).length;
    // Gelen faturalara UKBA Kasa'dan yanit verilmiyor; rozet "yanit bekleyen"
    // degil, secili donemdeki gelen fatura sayisini gosterir.
    const inboundPending = (inboundInvoices || []).length;
    return {
      pendingApproval: draftStats.pendingApproval,
      pendingApprovalAmount,
      missing: recentMissing,
      sendReady: draftStats.sendReady,
      error: recentError,
      sent: draftStats.sent,
      unmatchedMovements: unmatchedMovements.length,
      toInvoiceTotal: unmatchedAmount,
      inboundPending
    };
  }, [queueDateScopedMatches, draftStats, unmatchedMovements, sube, invoiceSettingsWithHas, inboundInvoices]);

  /**
   * Genel Bakış ve Giden Belgeler ekranları Faturalar listesine birleştirildi.
   * Eski hedeflere yapılan her gezinme, listenin doğru filtresine yönlenir —
   * böylece "bu fatura hangi ekranda?" sorusu ortadan kalkar.
   */
  const handleNavigate = useCallback((nav, opts = {}) => {
    const MERGED_INTO_LIST = {
      [INVOICE_NAV.OVERVIEW]: DRAFT_FILTER.PENDING_APPROVAL,
      [INVOICE_NAV.NEEDS_ACTION]: DRAFT_FILTER.ERROR
    };

    // Giden faturalar ayri sayfada (portal arsivi + sistemden kesilenler)
    if (nav === INVOICE_NAV.KOLAYSOFT_ARCHIVE || (nav === INVOICE_NAV.APPROVAL && opts.draftFilter === DRAFT_FILTER.SENT)) {
      setActiveNav(INVOICE_NAV.OUTBOUND);
      return;
    }

    if (Object.prototype.hasOwnProperty.call(MERGED_INTO_LIST, nav)) {
      setActiveNav(INVOICE_NAV.APPROVAL);
      setDraftFilter(opts.draftFilter || MERGED_INTO_LIST[nav]);
      return;
    }

    setActiveNav(nav);
    if (opts.draftFilter) setDraftFilter(opts.draftFilter);
    if (nav === INVOICE_NAV.APPROVAL && !opts.draftFilter) {
      setDraftFilter(DRAFT_FILTER.PENDING_APPROVAL);
    }
  }, []);

  const handleCreateStandaloneInvoice = () => {
    // Kaydet’e basılana kadar Firestore’a yazma — yalnızca yerel form
    setCreatingMatch(false);
    setActiveNav(INVOICE_NAV.NEW_INVOICE);
    setSaveSuccessMessage('');
    const draft = normalizeInvoiceDraft(
      {
        source: 'manual-scratch',
        items: [createEmptyLineItem()],
        // Esleşmeden gelen taslaklardaki gibi subeye gore not; elle yazilinca
        // Sefakoy'de "MERKEZ MAGAZADAN" yazilmisti (NIHAT DEMIR, 02.10.2026).
        invoiceNote: buildBranchInvoiceNote(sube),
        // Il/ilce subeye gore dolu gelir (Merkez/Sefakoy: Kucukcekmece,
        // Kavakli: Beylikduzu); istisnada personel degistirir (05.10.2026).
        city: getDefaultBranchInvoiceLocation(sube).city,
        customerIl: getDefaultBranchInvoiceLocation(sube).city,
        district: getDefaultBranchInvoiceLocation(sube).district,
        customerIlce: getDefaultBranchInvoiceLocation(sube).district
      },
      invoiceSettingsWithHas
    );
    const localMatch = {
      id: `unsaved__${Date.now()}`,
      _localOnly: true,
      bankMovementId: 'standalone',
      bankMovementData: {},
      matchType: INVOICE_MATCH_TYPE.STANDALONE,
      islemId: 'standalone',
      islemData: {},
      islemIds: [],
      islemlerData: [],
      totalSelectedAmount: 0,
      selectedIslemCount: 0,
      invoiceStatus: INVOICE_STATUS.DRAFT,
      customerStatus: 'missing',
      invoiceDraft: draft,
      manualMatched: true,
      autoMatched: false,
      sube
    };
    setSelectedDraftMatch(localMatch);
    setDraftForm(draft);
  };

  const renderMatchStatusChip = (linkStatus) => {
    const meta = getInvoiceMatchListChipMeta(linkStatus);
    if (!meta) {
      return (
        <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>-</Typography>
      );
    }
    return (
      <Chip
        size="small"
        label={meta.label}
        color={meta.color}
        variant={linkStatus?.cancelledOnly ? 'outlined' : 'filled'}
        sx={meta.sx}
      />
    );
  };

  const renderLinkedMatchAlert = (linkStatus, entityLabel) => {
    if (!linkStatus?.isLocked) return null;
    return (
      <Alert
        severity="info"
        icon={<WarningAmberIcon fontSize="inherit" />}
        sx={{ py: 0.4, '& .MuiAlert-message': { fontSize: 11.5, fontWeight: 700 } }}
      >
        Bu {entityLabel} zaten fatura taslağına bağlı. Taslaklar sekmesinden kontrol edebilirsiniz.
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#334155', mt: 0.4 }}>
          Taslak ID: {linkStatus.matchId} · Durum: {getInvoiceMatchListLabel(linkStatus)}
        </Typography>
      </Alert>
    );
  };

  return (
    <Dialog className="iv-modal" fullScreen open={open} onClose={onClose} PaperProps={{ sx: { bgcolor: iv.bg } }}>
      <Box
        sx={{
          height: iv.headerH,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
          bgcolor: iv.surface,
          borderBottom: `1px solid ${iv.border}`,
          flexShrink: 0
        }}
      >
        <DescriptionIcon sx={{ color: iv.primary, fontSize: 22 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: iv.text, lineHeight: 1.2 }}>Faturalar</Typography>
          <Typography sx={{ fontSize: 11.5, color: iv.textMuted, fontWeight: 600 }}>
            e-Fatura ve e-Arşiv · {branchLabel(sube)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {loadError ? (
          <Alert severity="warning" onClose={() => setLoadError('')} sx={{ py: 0, px: 1.2, maxWidth: 340, '& .MuiAlert-message': { fontSize: 12, fontWeight: 700 } }}>
            {loadError}
          </Alert>
        ) : null}
        {actionMessage ? (
          <Alert severity={actionMessage.type} onClose={() => setActionMessage(null)} sx={{ py: 0, px: 1.2, maxWidth: 300, '& .MuiAlert-message': { fontSize: 12, fontWeight: 700 } }}>
            {actionMessage.text}
          </Alert>
        ) : null}
        <Button
          size="small"
          onClick={handleRefresh}
          disabled={refreshing}
          startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
          sx={{ textTransform: 'none', fontWeight: 700, color: iv.text, border: `1px solid ${iv.border}`, bgcolor: '#fff' }}
        >
          Yenile
        </Button>
        <IconButton edge="end" onClick={onClose} aria-label="Kapat">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ height: `calc(100vh - ${iv.headerH}px)`, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        <InvoiceSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          activeNav={activeNav}
          onNavigate={handleNavigate}
          onNewInvoice={handleCreateStandaloneInvoice}
          creatingNew={creatingMatch && activeNav === INVOICE_NAV.NEW_INVOICE}
          badges={{
            approval: draftStats.pendingApproval,
            toInvoice: unmatchedMovements.length,
            outboundError: draftStats.error,
            inbound: (inboundInvoices || []).filter((r) => (r.inboundStatus || 'pending') === 'pending')
              .length
          }}
          locked={{ outbound: false, inbound: false, reports: false }}
        />

        <Box sx={{ flex: 1, minWidth: 0, p: 1.5, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {showFullPageLoader ? (
            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : activeNav === INVOICE_NAV.OVERVIEW ? (
            <InvoiceOverviewPanel
              stats={overviewStats}
              invoiceSettings={invoiceSettingsWithHas}
              matchDataLoading={matchDataLoading || refreshing}
              autoMatching={autoMatching || creatingMatch}
              onNavigate={handleNavigate}
              onRunAutoMatch={handleManualAutoMatch}
              onRefresh={handleRefresh}
            />
          ) : activeNav === INVOICE_NAV.NEW_INVOICE ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, minHeight: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
                <Stack direction="row" alignItems="baseline" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: iv.text, letterSpacing: '-0.02em' }}>
                    Yeni Fatura
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: iv.textMuted, fontWeight: 600 }}>
                    Müşteri → Satırlar → Kaydet / Onaya Gönder
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleNavigate(INVOICE_NAV.APPROVAL)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 12.5,
                    borderColor: iv.border,
                    color: iv.text,
                    bgcolor: '#fff',
                    flexShrink: 0
                  }}
                >
                  Onay kuyruğu
                </Button>
              </Box>
              {creatingMatch && !selectedDraftMatch ? (
                <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    ...ivSurfaceSx,
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    borderRadius: '10px'
                  }}
                >
                  <InvoiceDraftDetailPanel
                    match={selectedDraftMatch}
                    form={draftForm}
                    sube={sube}
                    amlStatus={selectedDraftMatch?.id ? amlByMatchId[selectedDraftMatch.id] || 'clear' : null}
                    onChange={setDraftForm}
                    onSave={handleSaveDraft}
                    onMarkReady={handleMarkReady}
                    onRevertToDraft={selectedDraftMatch?._digerSube ? null : handleRevertToDraft}
                    onSaveReadyEdit={handleSaveReadyEdit}
                    onCancelReadyEdit={handleCancelReadyEdit}
                    onCancel={handleCancelDraft}
                    onDelete={handleDeleteCancelledDraft}
                    onSendToEdm={() => handleSendToEdm()}
                    onRefreshEdmStatus={() => handleRefreshEdmStatus()}
                    saving={savingDraft}
                    statusActionLoading={statusActionLoading}
                    sendLoading={sendActionLoading}
                    saveSuccess={saveSuccessMessage}
                    invoiceSettings={invoiceSettingsWithHas}
                    density="comfortable"
                  />
                </Paper>
              )}
            </Box>
          ) : activeNav === INVOICE_NAV.TO_INVOICE ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: selectedMovement ? '1.15fr 0.85fr' : '1fr' },
                gap: 1.25
              }}
            >
              <InvoiceToInvoicePanel
                unmatchedMovements={unmatchedMovements}
                loading={matchDataLoading}
                autoMatching={autoMatching || creatingMatch}
                onRunAutoMatch={handleManualAutoMatch}
                selectedMovementId={selectedMovement?.id || null}
                onSelectMovement={(row) =>
                  setSelectedMovement((prev) => (prev?.id === row.id ? null : row))
                }
                onHideMovement={handleHideMovement}
              />
              {selectedMovement ? (
                <Paper
                  variant="outlined"
                  sx={{ ...ivSurfaceSx, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                  <Box sx={{ ...panelHeaderSx, bgcolor: '#fff', flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }} noWrap>
                        {truncateSenderDisplay(resolveMovementSenderName(selectedMovement), 26)}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: iv.textMuted, fontWeight: 700 }}>
                        {formatMoney(selectedMovement.amount)} TL ·{' '}
                        {formatDateTime(selectedMovement.bookingDate || selectedMovement.createdAt)}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setSelectedMovement(null)} aria-label="Kapat">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    {movementSuggestions.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 13, color: iv.textMuted, fontWeight: 700 }}>
                          Bu hareket için aday işlem bulunamadı.
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: iv.textMuted, mt: 0.6, lineHeight: 1.6 }}>
                          Aynı tutarda ve aynı günde bir işlem yok, ya da işlemin müşterisi
                          bu hareketin göndereniyle çelişiyor.
                        </Typography>
                      </Box>
                    ) : (
                      movementSuggestions.map((row, index) => {
                        const adSiz = row.nameMatchLevel === 'unknown';
                        return (
                          <Box
                            key={`sug-${row.islem?.id}-${index}`}
                            sx={{
                              px: 1.4,
                              py: 1.1,
                              borderBottom: `1px solid ${iv.border}`,
                              bgcolor: index % 2 === 0 ? '#fff' : '#fafbfc'
                            }}
                          >
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: iv.text }} noWrap>
                                  {row.islem?.islemNo || row.islem?.id}
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, color: iv.textMuted, mt: 0.2 }} noWrap>
                                  {resolveIslemMusteriName(row.islem) || 'Müşteri girilmemiş'} ·{' '}
                                  {formatMoney(row.islem?.havaleEft)} TL
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: iv.textMuted, mt: 0.2 }}>
                                  {formatDateTime(row.islem?.timestamp)}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={row.score}
                                color={matchStatusColor(row.status)}
                                sx={{ fontWeight: 900, minWidth: 42 }}
                              />
                            </Stack>

                            {adSiz ? (
                              <Chip
                                size="small"
                                icon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />}
                                label={nameMatchLevelLabel(row.nameMatchLevel)}
                                sx={{
                                  mt: 0.7,
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  bgcolor: '#fff7ed',
                                  color: '#b45309',
                                  border: '1px solid #fcd9a4'
                                }}
                              />
                            ) : null}

                            <Box sx={{ mt: 0.8, display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={creatingMatch}
                                onClick={() =>
                                  adSiz || row.score < INVOICE_MATCH_THRESHOLDS.matched
                                    ? openManualMatchDialog(selectedMovement, row.islem, row)
                                    : handleCreateMatch(selectedMovement, row.islem, row.score)
                                }
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: 12,
                                  bgcolor: iv.primary,
                                  boxShadow: 'none',
                                  '&:hover': { bgcolor: iv.primaryHover, boxShadow: 'none' }
                                }}
                              >
                                Eşleştir
                              </Button>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Paper>
              ) : null}
            </Box>
          ) : activeNav === INVOICE_NAV.OUTBOUND || activeNav === INVOICE_NAV.NEEDS_ACTION ? (
            // Giden faturalar GİB'e gitmiş belgeler; düzenleme paneli yok, satır resmi görünümü açar
            <InvoiceSentPanel sube={sube} invoiceSettings={invoiceSettings} matches={invoiceMatches} />
          ) : activeNav === INVOICE_NAV.EXPENSE_NOTE ? (
            <InvoiceExpenseNotePanel
              islemler={islemler}
              kesilmisIslemIds={gpKesilmisIslemIds}
              isaretler={gpIsaretler}
              onToggleKesildi={async (aday, kesildi) => {
                const personel = getPersonelPayload();
                if (!personel) {
                  toast.error('Personel oturumu bulunamadı.');
                  return;
                }
                try {
                  await setGiderPusulasiKesildi({ sube, islem: aday, kesildi, personel });
                  toast.success(kesildi ? 'Kesildi olarak işaretlendi' : 'Kesilmedi olarak işaretlendi');
                } catch (err) {
                  toast.error(
                    err?.code === 'permission-denied'
                      ? 'İşaret kaydedilemedi: yetki yok.'
                      : err?.message || 'İşaret kaydedilemedi.'
                  );
                }
              }}
              loading={matchDataLoading}
              onSave={handleSaveGiderPusulasi}
              saving={gpSaving}
              settings={invoiceSettings}
              sube={sube}
              musteriler={musteriKartlari}
              month={gpMonth}
              son6={gpSon6}
              tumYil={gpTumYil}
              onMonth={(m, opt) => {
                if (!opt?.modKoru) {
                  setGpSon6(false);
                  setGpTumYil(false);
                }
                setGpMonth(m);
              }}
              onSon6={() => {
                setGpTumYil(false);
                setGpSon6((v) => !v);
              }}
              onTumYil={() => {
                setGpSon6(false);
                setGpTumYil((v) => !v);
              }}
            />
          ) : activeNav === INVOICE_NAV.INBOUND ? (
            <InvoiceInboundPanel
              sube={sube}
              rows={inboundInvoices}
              month={inboundMonth}
              son6={inboundSon6}
              tumYil={inboundTumYil}
              onMonth={(m, opt) => {
                if (!opt?.modKoru) {
                  setInboundSon6(false);
                  setInboundTumYil(false);
                }
                setInboundMonth(m);
              }}
              onSon6={() => {
                setInboundTumYil(false);
                setInboundSon6((v) => !v);
              }}
              onTumYil={() => {
                setInboundSon6(false);
                setInboundTumYil((v) => !v);
              }}
              loading={inboundLoading}
              syncing={inboundSyncing}
              respondingId={inboundRespondingId}
              onSync={handleSyncInbound}
              onRespond={handleRespondInbound}
            />
          ) : activeNav === INVOICE_NAV.REPORTS ? (
            <InvoiceReportsPanel
              matches={invoiceMatches}
              inboundRows={inboundInvoices}
              pendingApprovalCount={draftStats.pendingApproval}
              onGoOutbound={() =>
                handleNavigate(INVOICE_NAV.APPROVAL, { draftFilter: DRAFT_FILTER.SENT })
              }
              onGoInbound={() => handleNavigate(INVOICE_NAV.INBOUND)}
              onGoApproval={() =>
                handleNavigate(INVOICE_NAV.APPROVAL, { draftFilter: DRAFT_FILTER.PENDING_APPROVAL })
              }
            />
          ) : activeNav === INVOICE_NAV.AML ? (
            <Box sx={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Hızlı Sorgu bu panelin kendi sekmesi oldu; dış sekme katmanı kaldırıldı. */}
              <InvoiceIdentityRiskPanel sube={sube} invoiceSettings={invoiceSettingsWithHas} />
            </Box>
          ) : activeNav === INVOICE_NAV.QUICK_LOOKUP ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 900, color: iv.text, letterSpacing: '-0.02em' }}>
                  Hızlı Sorgu
                </Typography>
                <Typography sx={{ fontSize: 13, color: iv.textMuted, fontWeight: 600, mt: 0.25 }}>
                  TCKN / VKN ile resmi yaptırım listelerinde anında kontrol
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <InvoiceAmlPanel sube={sube} section="lookup" />
              </Box>
            </Box>
          ) : activeNav === INVOICE_NAV.SETTINGS ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <InvoiceSettingsPanel
                sube={sube}
                settings={invoiceSettings}
                loading={settingsLoading}
                canEdit={canEditInvoiceSettings}
              />
            </Box>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0 }}>
              {/* Başlık + tek eylem. Toplu düğmeler seçim yokken gösterilmiyor. */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={{ fontSize: isPhone ? 18 : 22, fontWeight: 800, color: iv.text }}>Faturalar</Typography>
                  {isPhone ? null : (
                    <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.3 }}>
                      Taslaktan GİB gönderimine kadar tüm faturalar tek listede
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={creatingMatch || autoMatching}
                  startIcon={autoMatching || creatingMatch ? <CircularProgress size={14} /> : <RefreshIcon />}
                  onClick={handleManualAutoMatch}
                  sx={{ textTransform: 'none', fontWeight: 700, borderColor: iv.border, color: iv.text, bgcolor: '#fff' }}
                >
                  Yeni Eşleşmeleri Bul
                </Button>
              </Box>

              {/* Sayaçlar ayrı bir "Genel Bakış" sayfası değil; tıklayınca listeyi süzer. */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(4, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: isPhone ? 0.6 : 1
                }}
              >
                {QUEUE_TILES.map((tile) => {
                  const value = tile.key === 'toInvoice' ? unmatchedMovements.length : draftStats[tile.statKey];
                  const isActive = tile.filter ? draftFilter === tile.filter : activeNav === INVOICE_NAV.TO_INVOICE;
                  return (
                    <Box
                      key={tile.key}
                      component="button"
                      type="button"
                      onClick={() =>
                        tile.filter ? setDraftFilter(tile.filter) : handleNavigate(INVOICE_NAV.TO_INVOICE)
                      }
                      sx={{
                        all: 'unset',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        px: isPhone ? 0.75 : 1.5,
                        py: isPhone ? 0.7 : 1.1,
                        minWidth: 0,
                        overflow: 'hidden',
                        borderRadius: '10px',
                        bgcolor: '#fff',
                        border: `1px solid ${isActive ? tile.color : iv.border}`,
                        boxShadow: isActive ? `inset 0 0 0 1px ${tile.color}` : 'none',
                        transition: 'border-color .15s ease',
                        '&:hover': { borderColor: tile.color }
                      }}
                    >
                      <Typography sx={{ fontSize: isPhone ? 9 : 11.5, fontWeight: 800, color: iv.textMuted, letterSpacing: isPhone ? 0 : '0.03em', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isPhone ? (tile.shortLabel || tile.label) : tile.label}
                      </Typography>
                      <Typography sx={{ fontSize: isPhone ? 17 : 24, fontWeight: 800, color: tile.color, lineHeight: 1.15, mt: 0.2 }}>
                        {value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Arama + durum filtreleri + tarih filtreleri tek satırda. Hiçbiri gizli değil. */}
              <Box
                sx={{
                  ...ivSurfaceSx,
                  px: 1.2,
                  py: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <TextField
                  size="small"
                  value={queueSearch}
                  onChange={(e) => setQueueSearch(e.target.value)}
                  placeholder="Müşteri, TCKN, tutar, fatura no…"
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ fontSize: 18, color: iv.textMuted, mr: 0.75 }} />,
                    endAdornment: queueSearch ? (
                      <IconButton size="small" onClick={() => setQueueSearch('')} aria-label="Aramayı temizle">
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    ) : null
                  }}
                  sx={{
                    flex: '0 1 240px',
                    minWidth: 170,
                    maxWidth: 260,
                    '& .MuiOutlinedInput-root': { bgcolor: '#fff', fontSize: 13 }
                  }}
                />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={draftFilter}
                  onChange={(_, value) => value && setDraftFilter(value)}
                  sx={{
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 12,
                      px: 1.1,
                      border: `1px solid ${iv.border}`,
                      borderRadius: '8px !important',
                      mr: 0.5,
                      mb: 0.25
                    }
                  }}
                >
                  <ToggleButton value={DRAFT_FILTER.PENDING_APPROVAL}>
                    Onay Bekliyor ({draftStats.pendingApproval})
                  </ToggleButton>
                  <ToggleButton value={DRAFT_FILTER.MISSING}>Bilgi Eksik ({draftStats.missing})</ToggleButton>
                  <ToggleButton value={DRAFT_FILTER.SEND_READY}>Onaylandı ({draftStats.sendReady})</ToggleButton>
                  <ToggleButton value={DRAFT_FILTER.ERROR}>Hata ({draftStats.error})</ToggleButton>
                  <ToggleButton value={DRAFT_FILTER.CANCELLED}>İptal ({draftStats.cancelled})</ToggleButton>
                  <ToggleButton value={DRAFT_FILTER.ALL}>Tümü</ToggleButton>
                </ToggleButtonGroup>
                {/* Tarih filtreleri satırın sağ ucunda kalsın */}
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={queueDateFilter}
                  onChange={(_, value) => value && setQueueDateFilter(value)}
                  sx={{
                    ml: 'auto',
                    flexShrink: 0,
                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.1 }
                  }}
                >
                  {QUEUE_DATE_FILTER_OPTIONS.map((option) => (
                    <ToggleButton key={option.value} value={option.value}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* Toplu işlem çubuğu — yalnızca seçim varken. */}
              {selectedDraftIds.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: '10px',
                    bgcolor: iv.primarySoft,
                    border: `1px solid ${iv.primary}`
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: iv.primary, mr: 0.5 }}>
                    {selectedDraftIds.length} fatura seçildi
                  </Typography>
                  {draftFilter === DRAFT_FILTER.CANCELLED ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      disabled={statusActionLoading}
                      onClick={handleBatchDeleteCancelled}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Seçilenleri Sil
                    </Button>
                  ) : (
                    <>
                      {!isSendReadyFilter ? (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={batchApproving}
                        startIcon={batchApproving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                        onClick={handleBatchApprove}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          bgcolor: iv.primary,
                          boxShadow: 'none',
                          '&:hover': { bgcolor: iv.primaryHover, boxShadow: 'none' }
                        }}
                      >
                        Onayla
                      </Button>
                      ) : null}
                      {draftFilter === DRAFT_FILTER.SEND_READY || draftFilter === DRAFT_FILTER.ERROR ? (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={batchSending || !batchSendProviderOk}
                        startIcon={batchSending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                        onClick={() => handleBatchSendToEdm()}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          bgcolor: '#0f766e',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#0d9488', boxShadow: 'none' }
                        }}
                      >
                        GİB'e Gönder
                      </Button>
                      ) : null}
                    </>
                  )}
                  <Button
                    size="small"
                    onClick={() => setSelectedDraftIds([])}
                    sx={{ textTransform: 'none', fontWeight: 700, color: iv.textMuted, ml: 'auto' }}
                  >
                    Seçimi temizle
                  </Button>
                </Box>
              ) : null}

              {(autoMatching || creatingMatch) ? (
                <Alert severity="info" sx={{ py: 0.3, '& .MuiAlert-message': { fontSize: 12, fontWeight: 700 } }}>
                  Banka hareketleri ile işlemler otomatik eşleştiriliyor…
                </Alert>
              ) : null}
              {autoMatchResult?.created > 0 ? (
                <Alert severity="success" sx={{ py: 0.3, '& .MuiAlert-message': { fontSize: 12, fontWeight: 700 } }}>
                  Son otomatik eşleştirmede {autoMatchResult.created} fatura taslağı oluşturuldu.
                </Alert>
              ) : null}

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.25fr' },
                  gap: 1.25
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    ...ivSurfaceSx,
                    minHeight: 0,
                    display: isPhone && selectedDraftMatch ? 'none' : 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                >
                  {/* "Kuyruk" başlığı kaldırıldı — sayı zaten üstteki durum filtrelerinde yazıyor. */}
                  <TableContainer className="iv-draft-table-wrap" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <Table className="iv-draft-table" stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={tableHeadCellSx}>
                            <Checkbox
                              size="small"
                              checked={
                                draftFilter === DRAFT_FILTER.CANCELLED
                                  ? allCancelledSelected
                                  : isSendReadyFilter
                                    ? allSendableSelected
                                    : allApprovableSelected
                              }
                              indeterminate={
                                draftFilter === DRAFT_FILTER.CANCELLED
                                  ? selectedDraftIds.length > 0 &&
                                    !allCancelledSelected &&
                                    cancelledFilteredIds.some((id) => selectedDraftIds.includes(id))
                                  : isSendReadyFilter
                                    ? selectedDraftIds.length > 0 &&
                                      !allSendableSelected &&
                                      sendableFilteredIds.some((id) => selectedDraftIds.includes(id))
                                  : selectedDraftIds.length > 0 &&
                                    !allApprovableSelected &&
                                    approvableFilteredIds.some((id) => selectedDraftIds.includes(id))
                              }
                              disabled={
                                draftFilter === DRAFT_FILTER.CANCELLED
                                  ? cancelledFilteredIds.length === 0
                                  : isSendReadyFilter
                                    ? sendableFilteredIds.length === 0
                                    : approvableFilteredIds.length === 0
                              }
                              onChange={
                                draftFilter === DRAFT_FILTER.CANCELLED
                                  ? handleToggleSelectAllCancelled
                                  : isSendReadyFilter
                                    ? handleToggleSelectAllSendable
                                    : handleToggleSelectAllApprovable
                              }
                            />
                          </TableCell>
                          <TableCell sx={tableHeadCellSx}>Müşteri</TableCell>
                          <TableCell sx={tableHeadCellSx}>Tarih</TableCell>
                          <TableCell sx={tableHeadCellSx}>İşlem</TableCell>
                          <TableCell align="right" sx={tableHeadCellSx}>Tutar</TableCell>
                          <TableCell sx={tableHeadCellSx}>Durum</TableCell>
                          {/* Giden faturalara bakarken belge sütunları açılır —
                              ayrı bir "Giden Belgeler" ekranına gitmeye gerek yok. */}
                          {showDocumentColumns ? (
                            <>
                              <TableCell sx={tableHeadCellSx}>Fatura No</TableCell>
                              <TableCell sx={tableHeadCellSx}>Tip</TableCell>
                            </>
                          ) : null}
                          <TableCell sx={tableHeadCellSx}>Risk</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dateFilteredDrafts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={showDocumentColumns ? 9 : 7} align="center" sx={{ py: 3, color: '#64748b', fontWeight: 700 }}>
                              {draftFilter === DRAFT_FILTER.PENDING_APPROVAL
                                ? 'Seçilen tarihte onay bekleyen fatura yok.'
                                : queueDateFilter !== INVOICE_DATE_FILTER.ALL
                                  ? 'Seçilen tarih aralığında kayıt yok. Tümü veya daha geniş tarih deneyin.'
                                  : 'Bu filtreye uygun kayıt yok.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedDrafts.map((row) => {
                            const isCancelled = row.invoiceStatus === INVOICE_STATUS.CANCELLED;
                            const isReady = row.invoiceStatus === INVOICE_STATUS.READY;
                            const customerMissing =
                              !isCancelled &&
                              !isReady &&
                              resolveLiveCustomerStatus(row, sube, invoiceSettingsWithHas) === 'missing';
                            // Diger subenin taslagi da onaylanabilir (kaydin kendi subesiyle degerlendirilir)
                            const canApprove = canMarkInvoiceReady(row, row._sube || sube, invoiceSettingsWithHas);
                            const onayaHazir = canApprove;
                            const isSentLike =
                              row.invoiceStatus === INVOICE_STATUS.SENT ||
                              row.invoiceStatus === INVOICE_STATUS.APPROVED;
                            // Gönderilmiş / Kolaysoft'ta kesilmiş kayıt "Onaya Hazır" diye görünmesin.
                            const queueStatusMeta = isSentLike
                              ? getMatchStatusMeta(row)
                              : isCancelled
                              ? getInvoiceStatusMeta(INVOICE_STATUS.CANCELLED)
                              : isReady
                                ? getInvoiceStatusMeta(INVOICE_STATUS.READY)
                                : customerMissing
                                  ? { label: 'Bilgi Eksik', color: 'warning' }
                                  : onayaHazir
                                    ? { label: 'Onaya Hazır', color: 'success' }
                                    : { label: 'Taslak', color: 'default', sx: { bgcolor: '#e2e8f0', color: '#475569', fontWeight: 800 } };
                            return (
                              <TableRow
                                className="iv-draft-row"
                                key={row.id}
                                hover
                                selected={selectedDraftMatch?.id === row.id}
                                onClick={() => handleSelectDraft(row)}
                                sx={{
                                  cursor: 'pointer',
                                  opacity: isCancelled ? 0.55 : 1,
                                  bgcolor: isCancelled ? '#f8fafc' : 'inherit',
                                  '& .MuiTableCell-root': { py: 0.8, fontSize: 11.5 }
                                }}
                              >
                                <TableCell className="iv-c-check" padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    size="small"
                                    disabled={
                                      draftFilter === DRAFT_FILTER.CANCELLED
                                        ? !isCancelled
                                        : isSendReadyFilter
                                          ? row.invoiceStatus !== INVOICE_STATUS.READY
                                          : !canApprove
                                    }
                                    checked={selectedDraftIds.includes(row.id)}
                                    onChange={(e) => handleToggleDraftSelect(row.id, e)}
                                  />
                                </TableCell>
                                <TableCell className="iv-c-musteri" sx={{ fontWeight: 700 }}>
                                  {row.invoiceDraft?.title ||
                                    row.invoiceDraft?.customerName ||
                                    formatMusteriAdi(row.islemData?.musteri) ||
                                    '-'}
                                  {row._digerSube ? (
                                    <Chip
                                      size="small"
                                      label={SUBE_ETIKET[row._sube] || row._sube}
                                      title="Başka şubenin faturası (ortak liste) — onay o şubede, gönderim buradan da yapılabilir"
                                      sx={{ ml: 0.6, height: 18, fontSize: 10, fontWeight: 800, bgcolor: '#ede9fe', color: '#6d28d9' }}
                                    />
                                  ) : null}
                                  {isKrediKartiMatch(row) ? (
                                    <Chip
                                      size="small"
                                      label="Kredi Kartı"
                                      sx={{
                                        ml: 0.6,
                                        height: 18,
                                        fontSize: 10,
                                        fontWeight: 800,
                                        bgcolor: '#ede9fe',
                                        color: '#6d28d9'
                                      }}
                                    />
                                  ) : (
                                    /* Her satirda "Otomatik" / "Sifirdan" yazmak
                                       musteri adina yer birakmiyordu; tek harf
                                       yeter, aciklamasi tooltip'te. */
                                    <Tooltip title={row.autoMatched ? 'Otomatik eşleşme' : 'Elle oluşturuldu'}>
                                      <Chip
                                        size="small"
                                        label={row.autoMatched ? 'O' : 'M'}
                                        sx={{ ml: 0.6, height: 18, minWidth: 18, fontSize: 10, fontWeight: 800, '& .MuiChip-label': { px: 0.6 } }}
                                      />
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell className="iv-c-tarih" sx={{ whiteSpace: 'nowrap', color: '#64748b', fontWeight: 700 }}>
                                  {formatDraftListDate(row)}
                                </TableCell>
                                <TableCell className="iv-c-islem">
                                  {isKrediKartiMatch(row)
                                    ? row.islemData?.islemNo || row.krediKartiIslemId
                                    : row.matchType === 'standalone'
                                    ? 'Sıfırdan'
                                    : row.matchType === 'multi'
                                      ? `${row.selectedIslemCount || row.islemIds?.length || 0} işlem`
                                      : row.islemData?.islemNo || row.islemId}
                                </TableCell>
                                <TableCell className="iv-c-tutar" align="right" sx={{ fontWeight: 800, color: iv.primary, whiteSpace: 'nowrap' }}>
                                  {formatMoney(row.invoiceDraft?.amount || row.totalSelectedAmount)} TL
                                </TableCell>
                                <TableCell className="iv-c-durum">
                                  {/* Rozet satirin yarisini kapliyordu; yukseklik ve
                                      yazi kuculdu, yanlardaki bosluk kisildi. */}
                                  <Chip
                                    size="small"
                                    color={queueStatusMeta.color}
                                    label={queueStatusMeta.label}
                                    sx={{
                                      fontWeight: 800,
                                      height: 19,
                                      fontSize: 10,
                                      '& .MuiChip-label': { px: 0.7 },
                                      ...queueStatusMeta.sx
                                    }}
                                  />
                                </TableCell>
                                {showDocumentColumns ? (
                                  <>
                                    <TableCell className="iv-c-fno" sx={{ fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                      {row.invoiceDraft?.invoiceNumber || row.providerRef?.invoiceId || '—'}
                                    </TableCell>
                                    <TableCell className="iv-c-tip" sx={{ fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                      {resolveDocTypeLabel(row)}
                                    </TableCell>
                                  </>
                                ) : null}
                                <TableCell className="iv-c-risk">
                                  <InvoiceAmlRiskChip status={amlByMatchId[row.id] || 'clear'} display="dot" />
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={dateFilteredDrafts.length}
                    page={Math.max(0, draftPagination.page - 1)}
                    onPageChange={(_, newPage) => setDraftPage(newPage + 1)}
                    rowsPerPage={INVOICE_LIST_PAGE_SIZE}
                    rowsPerPageOptions={[INVOICE_LIST_PAGE_SIZE]}
                    labelRowsPerPage=""
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                    sx={{
                      borderTop: `1px solid ${iv.border}`,
                      flexShrink: 0,
                      '.MuiTablePagination-toolbar': { minHeight: 40, px: 1 },
                      '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        fontSize: 11.5,
                        fontWeight: 700
                      }
                    }}
                  />
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    ...ivSurfaceSx,
                    minHeight: 0,
                    display: isPhone && !selectedDraftMatch ? 'none' : 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: '#fbfdff'
                  }}
                >
                  {isPhone && selectedDraftMatch ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedDraftMatch(null);
                        setDraftForm(null);
                      }}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 800, m: 0.75 }}
                    >
                      ‹ Listeye dön
                    </Button>
                  ) : null}
                  <InvoiceDraftDetailPanel
                    match={selectedDraftMatch}
                    form={draftForm}
                    sube={sube}
                    amlStatus={selectedDraftMatch?.id ? amlByMatchId[selectedDraftMatch.id] || 'clear' : null}
                    onChange={setDraftForm}
                    onSave={handleSaveDraft}
                    onMarkReady={handleMarkReady}
                    onRevertToDraft={selectedDraftMatch?._digerSube ? null : handleRevertToDraft}
                    onSaveReadyEdit={handleSaveReadyEdit}
                    onCancelReadyEdit={handleCancelReadyEdit}
                    onCancel={handleCancelDraft}
                    onDelete={handleDeleteCancelledDraft}
                    onSendToEdm={() => handleSendToEdm()}
                    onRefreshEdmStatus={() => handleRefreshEdmStatus()}
                    saving={savingDraft}
                    statusActionLoading={statusActionLoading}
                    sendLoading={sendActionLoading}
                    saveSuccess={saveSuccessMessage}
                    invoiceSettings={invoiceSettingsWithHas}
                  />
                </Paper>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={manualDialogOpen} onClose={() => setManualDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Manuel Eşleştirme</DialogTitle>
        <DialogContent dividers>
          {pendingManualPair &&
          (!pendingManualPair.nameMatch ||
            !pendingManualPair.amountMatch ||
            pendingManualPair.nameMatchLevel === 'weak' ||
            pendingManualPair.nameMatchLevel === 'none') ? (
            <Alert severity="warning" sx={{ mb: 1.5, py: 0.4, '& .MuiAlert-message': { fontSize: 12.5, fontWeight: 700 } }}>
              {!pendingManualPair.amountMatch && pendingManualPair.nameMatch
                ? 'Aynı isim bulundu, tutar farklı. Manuel çoklu eşleştirme yapıyorsunuz.'
                : 'İsim ve gönderen bilgisi uyuşmuyor. Manuel eşleştirme yapıyorsunuz.'}
            </Alert>
          ) : null}
          <Typography sx={{ fontSize: 13, color: '#475569', mb: 1.5 }}>
            Bu eşleştirme manuel olarak onaylanacak ve taslak oluşturulacak.
          </Typography>
          <TextField
            label="Manuel eşleştirme notu (opsiyonel)"
            fullWidth
            multiline
            minRows={2}
            value={manualMatchReason}
            onChange={(e) => setManualMatchReason(e.target.value)}
            placeholder="Örn: Müşteri telefonla teyit etti"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialogOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={creatingMatch}
            onClick={handleManualMatchConfirm}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Onayla ve Oluştur
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(amlConfirm)} onClose={() => setAmlConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" /> AML uyarısı
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, mb: 1.5 }}>
            Aşağıdaki alıcı{amlConfirm?.items?.length > 1 ? 'lar' : ''} yaptırım / kara liste kayıtlarındaki bir isim veya
            kimlikle eşleşiyor. Fatura engellenmez; onaya göndermeden önce kişinin gerçekten o kayıt olmadığını kontrol edin.
          </Typography>
          <Stack spacing={0.75}>
            {(amlConfirm?.items || []).map((it, i) => (
              <Stack key={`${it.name}-${i}`} direction="row" alignItems="center" spacing={1}>
                <Chip
                  size="small"
                  color={it.status === 'strong' ? 'error' : 'warning'}
                  label={it.status === 'strong' ? 'Kritik' : 'İnceleme'}
                  sx={{ fontWeight: 800 }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{it.name}</Typography>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAmlConfirm(null)} sx={{ textTransform: 'none' }}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const confirm = amlConfirm?.onConfirm;
              setAmlConfirm(null);
              confirm?.();
            }}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Yine de Onaya Gönder
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(dateOrderConfirm)} onClose={() => setDateOrderConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" /> Tarih sırası uyarısı
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, mb: 1.5 }}>
            Fatura numaraları üç şubede tek seriden veriliyor. Numara sırası tarih sırasını bozmasın diye
            önce aşağıdakileri halledin.
          </Typography>
          {(dateOrderConfirm?.payload?.oncekiBekleyenler || []).length ? (
            <>
              <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.5 }}>Önce bu onaylı faturaları kesin:</Typography>
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                {dateOrderConfirm.payload.oncekiBekleyenler.map((f) => (
                  <Stack key={`${f.sube}-${f.matchId}`} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={SUBE_ETIKET[f.sube] || f.sube} sx={{ fontWeight: 700 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{f.title || '-'}</Typography>
                    <Typography sx={{ fontSize: 13 }}>{String(f.invoiceDate).split('-').reverse().join('.')}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, minWidth: 90, textAlign: 'right' }}>
                      {Number(f.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : null}
          {(dateOrderConfirm?.payload?.dahaYeniKesilmis || []).length ? (
            <>
              <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.5 }}>
                Daha yeni tarihli fatura zaten kesilmiş — bu faturanın düzenlenme tarihini bugüne çekin:
              </Typography>
              <Stack spacing={0.5}>
                {dateOrderConfirm.payload.dahaYeniKesilmis.slice(0, 5).map((f) => (
                  <Stack key={`${f.sube}-${f.matchId}`} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={SUBE_ETIKET[f.sube] || f.sube} sx={{ fontWeight: 700 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
                      {f.invoiceNumber || ''} {f.title || ''}
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>{String(f.invoiceDate).split('-').reverse().join('.')}</Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateOrderConfirm(null)} sx={{ textTransform: 'none' }}>
            Vazgeç
          </Button>
          <Button
            color="warning"
            onClick={() => {
              const confirm = dateOrderConfirm?.onConfirm;
              setDateOrderConfirm(null);
              confirm?.();
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Yine de Gönder
          </Button>
          {dateOrderConfirm?.onIncludeOlder ? (
            <Button
              variant="contained"
              color="success"
              disableElevation
              onClick={() => {
                const dahil = dateOrderConfirm?.onIncludeOlder;
                setDateOrderConfirm(null);
                dahil?.();
              }}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Önce Bunları da Gönder (tarih sırasıyla)
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Dialog>
  );

}

export default InvoiceModal;
